import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { httpsCallable } from 'firebase/functions';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L, { LatLngExpression, LatLngLiteral, LeafletMouseEvent } from 'leaflet';
import {
  ArrowPathIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  MapPinIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { functions } from '../../firebase';
import { serviceProviderService } from '../../services/serviceProviderService';
import { geocodingService } from '../../services/geocodingService';
import { getUserRoleIdentity } from '../../utils/accessControl';
import { resolveServiceVerificationStatus } from '../../utils/serviceVerification';
import {
  resolveServiceProviderImageStoragePath,
  resolveServiceProviderImageUrl,
} from '../../utils/serviceProviderImage';
import { ServiceProviderProfileImage } from '../../types/firestore';
import {
  ServiceProviderProfileImageDraft,
  deleteServiceProviderProfileImageUpload,
  prepareServiceProviderProfileImageDraft,
  toServiceProviderProfileImageMutation,
  uploadServiceProviderProfileImageDraft,
} from '../../utils/serviceProviderImageUpload';
import {
  UserRole,
  type BusinessHours,
  type BusinessHoursDayKey,
  type ServiceAvailabilityOverride,
  type ServiceAvailabilityOverrideType,
  type ServiceProviderPricing,
  type ServiceProvider,
  type ServiceVerificationStatus,
  type ServiceCapabilities,
} from '../../types/firestore';
import {
  FULFILLMENT_MODES,
  FulfillmentMode,
  FulfillmentPricingHint,
  ServiceProviderFulfillmentConfig,
  getFulfillmentModeLabel,
  requiresFulfillmentAddress,
  resolveServiceFulfillmentCapabilities,
  type ServiceFulfillmentModeConfig,
  type ServiceFulfillmentModesConfig,
  type ServiceFulfillmentCapabilities,
  type FulfillmentProviderReadModel,
  type ResolvedFulfillmentModeCapability,
  type ServiceFulfillmentCapabilitiesState,
} from '../../shared/fulfillment';
import {
  BUSINESS_HOURS_DAY_LABELS,
  BUSINESS_HOURS_DAY_ORDER,
  BUSINESS_HOURS_TIME_ZONE,
  createDefaultBusinessHours,
  normalizeBusinessHours,
  validateBusinessHours,
} from '../../utils/businessHours';
import { Alert, Badge, Button, FormLabel, Input, Modal, Spinner } from '../common/UIComponents';
import ServiceCapabilitiesSection from './ServiceCapabilitiesSection';
import ServiceProviderImage from '../shared/ServiceProviderImage';
import { getFriendlyFirebaseRuntimeError } from '../../utils/firebaseRuntimeError';
import {
  formatServiceHourlyRateAmount,
  getPublicServiceHourlyRate,
  parseServiceHourlyRateAmountInput,
} from '../../utils/servicePricing';
import {
  cloneServiceCapabilities,
  createEmptyServiceCapabilities,
  formatServiceCapabilityIntents,
  providerCapabilityCategoryById,
  toCanonicalServiceCapabilities,
  validateServiceCapabilities,
} from '../../utils/serviceCapabilities';
import 'leaflet/dist/leaflet.css';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

interface ProfileFormData {
  serviceName: string;
  description: string;
  specializations: string[];
  businessHours: {
    timezone: 'Europe/Warsaw';
    weekly: Record<
      BusinessHoursDayKey,
      {
        enabled: boolean;
        open: string;
        close: string;
      }
    >;
  };
  availabilityOverride: {
    enabled: boolean;
    type: ServiceAvailabilityOverrideType;
    endsAt: string;
    message: string;
  };
  servicePricing: {
    hourlyRate: {
      public: boolean;
      amount: string;
    };
  };
  street: string;
  city: string;
  postalCode: string;
  phoneNumber: string;
  email: string;
}

interface PendingProfileImageState {
  draft: ServiceProviderProfileImageDraft;
  uploaded?: ServiceProviderProfileImage;
}

type LocationStatus = 'missing' | 'stale' | 'confirmed';
type ProfileSectionId =
  | 'overview'
  | 'serviceCapabilities'
  | 'pricingFulfillment'
  | 'availability'
  | 'location'
  | 'contact'
  | 'basic';
type BusinessHoursPreset = 'workdays' | 'everyday' | 'always' | 'custom';

interface LocationWorkflowState {
  coordinates: LatLngLiteral | null;
  status: LocationStatus;
}

interface NormalizedAddress {
  street: string;
  city: string;
  postalCode: string;
}

const DEFAULT_MAP_CENTER: LatLngExpression = [52.237049, 21.017532];
const DEFAULT_MAP_ZOOM = 6;
const CONFIRMED_MAP_ZOOM = 16;
const DESCRIPTION_TEXTAREA_MAX_HEIGHT = 220;
const DESCRIPTION_TEXTAREA_MIN_HEIGHT_MOBILE = 96;
const DESCRIPTION_TEXTAREA_MIN_HEIGHT_DESKTOP = 120;
export const SERVICE_FULFILLMENT_INSTRUCTIONS_MAX_LENGTH = 500;
const AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH = 160;

const AVAILABILITY_OVERRIDE_TYPE_LABELS: Record<ServiceAvailabilityOverrideType, string> = {
  vacation: 'Urlop',
  technical_break: 'Przerwa techniczna',
};

type FulfillmentModeFormState = {
  enabled: boolean;
  instructions: string;
  pricingHint: FulfillmentPricingHint;
};

export type ServiceFulfillmentFormState = Record<FulfillmentMode, FulfillmentModeFormState>;

type ServiceFulfillmentBuildResult =
  | {
      payload: ServiceProviderFulfillmentConfig;
      error: null;
    }
  | {
      payload?: undefined;
      error: string;
    };

type ServicePricingBuildResult =
  | {
      payload: ServiceProviderPricing;
      error: null;
    }
  | {
      payload?: undefined;
      error: string;
    };

const FULFILLMENT_PRICING_HINT_LABELS: Record<FulfillmentPricingHint, string> = {
  quote: 'Doliczane w wycenie',
  included: 'Wliczone w usługę',
  not_configured: 'Ustalane indywidualnie',
};

const FULFILLMENT_METHOD_CONTEXT: Record<FulfillmentMode, string> = {
  customerDropoff: 'Klient sam dostarcza sprzęt pod adres serwisu.',
  servicePickup: 'W kolejnym kroku klient poda adres odbioru sprzętu.',
  onsiteService: 'W kolejnym kroku klient poda miejsce wykonania naprawy.',
};

const createEmptyServiceFulfillmentFormState = (): ServiceFulfillmentFormState =>
  FULFILLMENT_MODES.reduce((acc, mode) => {
    acc[mode] = {
      enabled: false,
      instructions: '',
      pricingHint: 'not_configured',
    };
    return acc;
  }, {} as ServiceFulfillmentFormState);

const hasExplicitServiceFulfillmentConfig = (
  profile: FulfillmentProviderReadModel | null | undefined
): boolean =>
  Boolean(
    profile &&
      Object.prototype.hasOwnProperty.call(profile, 'serviceFulfillment') &&
      profile.serviceFulfillment !== undefined &&
      profile.serviceFulfillment !== null
  );

const capabilityToFulfillmentFormMode = (
  capability: ResolvedFulfillmentModeCapability
): FulfillmentModeFormState => ({
  enabled: capability.enabled,
  instructions: capability.instructions || '',
  pricingHint: capability.pricingHint || 'not_configured',
});

export const serviceFulfillmentToFormState = (
  provider: FulfillmentProviderReadModel | null | undefined
): ServiceFulfillmentFormState => {
  const emptyState = createEmptyServiceFulfillmentFormState();
  if (!hasExplicitServiceFulfillmentConfig(provider)) {
    return emptyState;
  }

  const capabilities = resolveServiceFulfillmentCapabilities(provider);
  if (capabilities.source !== 'providerProfile' || capabilities.state === 'invalid') {
    return emptyState;
  }

  return FULFILLMENT_MODES.reduce((acc, mode) => {
    acc[mode] = capabilityToFulfillmentFormMode(capabilities.modes[mode]);
    return acc;
  }, {} as ServiceFulfillmentFormState);
};

export const buildServiceFulfillmentPayload = (
  state: ServiceFulfillmentFormState
): ServiceFulfillmentBuildResult => {
  const enabledModes = FULFILLMENT_MODES.filter((mode) => state[mode]?.enabled === true);
  if (enabledModes.length === 0) {
    return { error: 'Wybierz co najmniej jeden sposób realizacji zleceń.' };
  }

  const modes: ServiceFulfillmentModesConfig = {};

  for (const mode of FULFILLMENT_MODES) {
    const modeState = state[mode] || createEmptyServiceFulfillmentFormState()[mode];
    const instructions = String(modeState.instructions || '').trim();
    if (instructions.length > SERVICE_FULFILLMENT_INSTRUCTIONS_MAX_LENGTH) {
      return {
        error: `Instrukcje dla "${getFulfillmentModeLabel(
          mode
        )}" są zbyt długie. Maksimum to ${SERVICE_FULFILLMENT_INSTRUCTIONS_MAX_LENGTH} znaków.`,
      };
    }

    const modePayload: ServiceFulfillmentModeConfig = {
      enabled: modeState.enabled === true,
      requiresAddress: requiresFulfillmentAddress(mode),
    };

    if (modePayload.enabled && instructions.length > 0) {
      modePayload.instructions = instructions;
    }
    if (modePayload.enabled && requiresFulfillmentAddress(mode)) {
      modePayload.pricingHint = modeState.pricingHint || 'not_configured';
    }

    modes[mode] = modePayload;
  }

  return {
    payload: {
      modes,
      payment: {
        cashOnly: true,
        onlinePaymentEnabled: false,
      },
    },
    error: null,
  };
};

const toLatLngLiteral = (raw: unknown): LatLngLiteral | null => {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;
  const latitudeCandidates = [candidate.latitude, candidate.lat, candidate._latitude];
  const longitudeCandidates = [
    candidate.longitude,
    candidate.lng,
    candidate.lon,
    candidate._longitude,
  ];
  const latitude = latitudeCandidates.find((value) => typeof value === 'number');
  const longitude = longitudeCandidates.find((value) => typeof value === 'number');

  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;

  return { lat: latitude, lng: longitude };
};

const normalizeAddress = (address: Partial<NormalizedAddress>): NormalizedAddress => ({
  street: (address.street || '').trim(),
  city: (address.city || '').trim(),
  postalCode: (address.postalCode || '').trim(),
});

const areAddressesEqual = (left: NormalizedAddress, right: NormalizedAddress): boolean =>
  left.street === right.street && left.city === right.city && left.postalCode === right.postalCode;

const normalizeSpecializationName = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pl-PL');

const dedupeSpecializationNames = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const cleaned = value.trim().replace(/\s+/g, ' ');
    const key = normalizeSpecializationName(cleaned);
    if (!cleaned || !key || seen.has(key)) return;
    seen.add(key);
    result.push(cleaned);
  });

  return result;
};

type BusinessHoursFormDay = ProfileFormData['businessHours']['weekly'][BusinessHoursDayKey];

const getBusinessHoursDaySummary = (day: BusinessHoursFormDay): string => {
  if (!day.enabled) {
    return 'Nieczynne';
  }
  if (day.open.trim() && day.close.trim()) {
    return `${day.open}–${day.close}`;
  }
  return 'Uzupełnij godziny';
};

const getBusinessHoursDayTone = (day: BusinessHoursFormDay): string => {
  if (!day.enabled) {
    return 'text-primary-light-400';
  }
  if (day.open.trim() && day.close.trim()) {
    return 'text-status-success-500';
  }
  return 'text-status-warning-500';
};

const BUSINESS_HOURS_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, '0')
);
const BUSINESS_HOURS_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, '0')
);

const normalizeTimeValue = (value: string | null | undefined): string => {
  const trimmed = String(value || '').trim();
  return /^\d{2}:\d{2}$/.test(trimmed) ? trimmed : '';
};

const splitTimeValue = (value: string | null | undefined): { hour: string; minute: string } => {
  const normalized = normalizeTimeValue(value);
  if (!normalized) {
    return { hour: '09', minute: '00' };
  }

  const [hour, minute] = normalized.split(':');
  return { hour, minute };
};

type BusinessHoursTimeFieldProps = {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => void;
};

const BusinessHoursTimeField: React.FC<BusinessHoursTimeFieldProps> = ({
  id,
  label,
  value,
  disabled = false,
  error,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [{ hour, minute }, setDraftTime] = useState(() => splitTimeValue(value));
  const normalizedValue = normalizeTimeValue(value);
  const modalTitleId = `${id}-dialog-title`;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const initialFocusRef = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraftTime(splitTimeValue(value));
  }, [isOpen, value]);

  const closePicker = () => setIsOpen(false);
  const clearTime = () => {
    onChange('');
    closePicker();
  };
  const confirmTime = () => {
    onChange(`${hour}:${minute}`);
    closePicker();
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm font-medium text-primary-light-200 mb-1">
        {label}
      </label>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
        className={`flex min-h-[40px] w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm focus-outline ${
          error
            ? 'border-status-error-500 bg-primary-dark-100/55 text-primary-light-100'
            : 'border-primary-dark-300/70 bg-primary-dark-100/55 text-primary-light-100'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-primary-dark-400'}`}
        aria-haspopup="dialog"
        aria-label={label}
      >
        <span className={normalizedValue ? 'font-medium tabular-nums' : 'text-primary-light-400'}>
          {normalizedValue || 'Wybierz godzinę'}
        </span>
        <ClockIcon className="h-5 w-5 flex-shrink-0 text-primary-light-400" aria-hidden="true" />
      </button>
      {error && <p className="mt-1 text-sm text-status-error-500">{error}</p>}

      <Modal
        isOpen={isOpen}
        onClose={closePicker}
        ariaLabelledBy={modalTitleId}
        initialFocusRef={initialFocusRef}
        returnFocusRef={triggerRef}
        overlayClassName="!z-[80] p-4 [padding-bottom:calc(1rem+env(safe-area-inset-bottom))]"
        className="!w-full !overflow-hidden !rounded-lg !border-primary-dark-300/70"
        style={{
          width: 'min(328px, calc(100vw - 32px))',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc((var(--app-vh, 1vh) * 100) - 32px - env(safe-area-inset-bottom))',
        }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-primary-dark-300/70 px-4 py-3">
          <div>
            <h3 id={modalTitleId} className="text-sm font-semibold text-primary-light-100">
              {label}
            </h3>
            <p className="mt-0.5 text-xs text-primary-light-400">
              Ustaw godzinę w strefie {BUSINESS_HOURS_TIME_ZONE}.
            </p>
          </div>
          <button
            type="button"
            onClick={closePicker}
            className="rounded-md p-1 text-primary-light-400 hover:text-primary-light-100 focus-outline"
            aria-label="Zamknij wybór godziny"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-3 px-4 py-4">
          <label className="min-w-0">
            <span className="mb-1 block text-xs font-medium text-primary-light-300">Godzina</span>
            <select
              ref={initialFocusRef}
              value={hour}
              onChange={(event) =>
                setDraftTime((current) => ({ ...current, hour: event.target.value }))
              }
              className="block h-11 w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/80 px-3 text-center text-base font-semibold tabular-nums text-primary-light-100 focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
            >
              {BUSINESS_HOURS_HOUR_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <span className="pb-2 text-lg font-semibold text-primary-light-300">:</span>
          <label className="min-w-0">
            <span className="mb-1 block text-xs font-medium text-primary-light-300">Minuty</span>
            <select
              value={minute}
              onChange={(event) =>
                setDraftTime((current) => ({ ...current, minute: event.target.value }))
              }
              className="block h-11 w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/80 px-3 text-center text-base font-semibold tabular-nums text-primary-light-100 focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
            >
              {BUSINESS_HOURS_MINUTE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-primary-dark-300/70 px-4 py-3 [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={clearTime}
            className="min-h-[40px] flex-1 rounded-lg border border-primary-dark-300/70 px-3 py-2 text-sm font-semibold text-primary-light-300 hover:bg-primary-dark-300/50 focus-outline max-[360px]:basis-full"
          >
            Wyczyść
          </button>
          <button
            type="button"
            onClick={closePicker}
            className="min-h-[40px] flex-1 rounded-lg border border-primary-dark-300/70 px-3 py-2 text-sm font-semibold text-primary-light-200 hover:bg-primary-dark-300/50 focus-outline max-[360px]:basis-[calc(50%-0.25rem)]"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={confirmTime}
            className="min-h-[40px] flex-1 rounded-lg bg-ratuj-400 px-3 py-2 text-sm font-semibold text-white shadow-button hover:bg-ratuj-300 focus-outline max-[360px]:basis-[calc(50%-0.25rem)]"
          >
            Ustaw
          </button>
        </div>
      </Modal>
    </div>
  );
};

const getFriendlyFunctionError = (err: any, fallback: string): string => {
  const code = err?.code || '';
  if (code.includes('permission-denied')) {
    return 'Nie masz uprawnien do wykonania tej akcji.';
  }
  if (code.includes('already-exists')) {
    return 'Taka specjalizacja juz istnieje lub oczekuje na zatwierdzenie.';
  }
  if (code.includes('invalid-argument')) {
    return 'Sprawdz dane formularza i sprobuj ponownie.';
  }
  if (code.includes('unauthenticated')) {
    return 'Sesja wygasla. Zaloguj sie ponownie.';
  }
  if (code.includes('internal') || code.includes('unavailable')) {
    return 'Wystapil chwilowy problem po stronie serwera. Sprobuj ponownie.';
  }
  return getFriendlyFirebaseRuntimeError(err, fallback);
};

const createDefaultBusinessHoursFormValue = (): ProfileFormData['businessHours'] => ({
  timezone: BUSINESS_HOURS_TIME_ZONE,
  weekly: {
    mon: { enabled: false, open: '', close: '' },
    tue: { enabled: false, open: '', close: '' },
    wed: { enabled: false, open: '', close: '' },
    thu: { enabled: false, open: '', close: '' },
    fri: { enabled: false, open: '', close: '' },
    sat: { enabled: false, open: '', close: '' },
    sun: { enabled: false, open: '', close: '' },
  },
});

const businessHoursToFormValue = (
  value: BusinessHours | null | undefined
): ProfileFormData['businessHours'] => {
  const normalized = normalizeBusinessHours(value) ?? createDefaultBusinessHours();
  return {
    timezone: BUSINESS_HOURS_TIME_ZONE,
    weekly: BUSINESS_HOURS_DAY_ORDER.reduce(
      (acc, dayKey) => {
        const day = normalized.weekly[dayKey];
        acc[dayKey] = {
          enabled: day.enabled,
          open: day.open || '',
          close: day.close || '',
        };
        return acc;
      },
      {} as ProfileFormData['businessHours']['weekly']
    ),
  };
};

const formBusinessHoursToPayload = (value: ProfileFormData['businessHours']): BusinessHours => ({
  timezone: BUSINESS_HOURS_TIME_ZONE,
  weekly: BUSINESS_HOURS_DAY_ORDER.reduce((acc, dayKey) => {
    const day = value.weekly[dayKey];
    acc[dayKey] = day.enabled
      ? {
          enabled: true,
          open: day.open.trim() || null,
          close: day.close.trim() || null,
        }
      : {
          enabled: false,
          open: null,
          close: null,
        };
    return acc;
  }, createDefaultBusinessHours().weekly),
  updatedAt: null,
});

const toDateFromFirestoreLike = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object') {
    const candidate = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof candidate.toDate === 'function') {
      const parsed = candidate.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }
    const seconds =
      typeof candidate.seconds === 'number'
        ? candidate.seconds
        : typeof candidate._seconds === 'number'
          ? candidate._seconds
          : null;
    if (seconds !== null) {
      const parsed = new Date(seconds * 1000);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  return null;
};

const toDatetimeLocalValue = (value: unknown): string => {
  const date = toDateFromFirestoreLike(value);
  if (!date) return '';
  const pad = (input: number) => String(input).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const availabilityOverrideToFormValue = (
  value: ServiceAvailabilityOverride | null | undefined
): ProfileFormData['availabilityOverride'] => ({
  enabled: value?.enabled === true,
  type: value?.type === 'technical_break' || value?.type === 'vacation' ? value.type : 'vacation',
  endsAt: toDatetimeLocalValue(value?.endsAt),
  message: String(value?.message || '').slice(0, AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH),
});

const formAvailabilityOverrideToPayload = (
  value: ProfileFormData['availabilityOverride']
): ServiceAvailabilityOverride => {
  const message = String(value.message || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH);
  return {
    enabled: value.enabled === true,
    type: value.type === 'technical_break' ? 'technical_break' : 'vacation',
    startsAt: null,
    endsAt: value.endsAt ? new Date(value.endsAt) : null,
    ...(message ? { message } : {}),
  };
};

const createDefaultServicePricingFormValue = (): ProfileFormData['servicePricing'] => ({
  hourlyRate: {
    public: false,
    amount: '',
  },
});

const servicePricingToFormValue = (
  profile: Pick<ServiceProvider, 'servicePricing'> | null | undefined
): ProfileFormData['servicePricing'] => {
  const hourlyRate = getPublicServiceHourlyRate(profile);
  if (!hourlyRate) {
    return createDefaultServicePricingFormValue();
  }

  return {
    hourlyRate: {
      public: true,
      amount: formatServiceHourlyRateAmount(hourlyRate.amount),
    },
  };
};

export const buildServicePricingPayload = (
  value: ProfileFormData['servicePricing']
): ServicePricingBuildResult => {
  const isPublic = value.hourlyRate.public === true;
  if (!isPublic) {
    return {
      payload: {
        hourlyRate: {
          public: false,
          currency: 'PLN',
        },
      },
      error: null,
    };
  }

  const parsedAmount = parseServiceHourlyRateAmountInput(value.hourlyRate.amount);
  if (parsedAmount.error || parsedAmount.amount === undefined) {
    return {
      error: parsedAmount.error || 'Podaj poprawną stawkę orientacyjną.',
    };
  }

  return {
    payload: {
      hourlyRate: {
        public: true,
        amount: parsedAmount.amount,
        currency: 'PLN',
      },
    },
    error: null,
  };
};

const getServiceCapabilitiesSummary = (value: ServiceCapabilities | null | undefined): string => {
  const firstItem = value?.items?.[0];
  if (!firstItem) return 'Nie ustawiono zakresu usług';
  const category = providerCapabilityCategoryById.get(firstItem.categoryId);
  const mode =
    firstItem.mode === 'categoryWide'
      ? 'cały obszar'
      : `${firstItem.objectSelections.length} ${
          firstItem.objectSelections.length === 1 ? 'rzecz' : 'rzeczy'
        }`;
  const suffix = value && value.items.length > 1 ? ` +${value.items.length - 1}` : '';
  return `${category?.label || 'Zakres usług'} · ${formatServiceCapabilityIntents(
    firstItem.intents
  )} · ${mode}${suffix}`;
};

const hasAnyBusinessHours = (value: ProfileFormData['businessHours'] | null | undefined): boolean =>
  Boolean(value && BUSINESS_HOURS_DAY_ORDER.some((dayKey) => value.weekly?.[dayKey]?.enabled));

const toggleInList = <T extends string>(source: T[], value: T): T[] =>
  source.includes(value) ? source.filter((item) => item !== value) : [...source, value];

const getAvailabilitySummary = (
  value: ProfileFormData['businessHours'] | null | undefined,
  override: ProfileFormData['availabilityOverride'] | null | undefined
): string => {
  if (override?.enabled) return 'Tymczasowa niedostępność ustawiona';
  if (!hasAnyBusinessHours(value)) return 'Nie ustawiono godzin';
  const enabledDays = BUSINESS_HOURS_DAY_ORDER.filter((dayKey) => value?.weekly?.[dayKey]?.enabled);
  const firstDay = enabledDays[0];
  const firstValue = firstDay ? value?.weekly?.[firstDay] : null;
  if (
    enabledDays.length === 5 &&
    enabledDays.every((dayKey) => ['mon', 'tue', 'wed', 'thu', 'fri'].includes(dayKey))
  ) {
    return `Pon-Pt · ${firstValue?.open || '--'}-${firstValue?.close || '--'}`;
  }
  if (enabledDays.length === 7) return 'Codziennie · wybrane godziny';
  return `${enabledDays.length} dni · wybrane godziny`;
};

const buildBusinessHoursPresetValue = (
  preset: Exclude<BusinessHoursPreset, 'custom'>
): ProfileFormData['businessHours'] => {
  const weekly = BUSINESS_HOURS_DAY_ORDER.reduce(
    (acc, dayKey) => {
      const enabled =
        preset === 'always' ||
        preset === 'everyday' ||
        (preset === 'workdays' && ['mon', 'tue', 'wed', 'thu', 'fri'].includes(dayKey));
      acc[dayKey] = {
        enabled,
        open: enabled ? (preset === 'always' ? '00:00' : '09:00') : '',
        close: enabled ? (preset === 'always' ? '23:59' : '17:00') : '',
      };
      return acc;
    },
    {} as ProfileFormData['businessHours']['weekly']
  );
  return {
    timezone: BUSINESS_HOURS_TIME_ZONE,
    weekly,
  };
};

const getFulfillmentSummary = (
  state: ServiceFulfillmentFormState,
  hasExplicit: boolean,
  pricing: ProfileFormData['servicePricing']
): string => {
  const methods = FULFILLMENT_MODES.filter((mode) => state[mode]?.enabled).map((mode) =>
    getFulfillmentModeLabel(mode)
  );
  const rate =
    pricing.hourlyRate.public && pricing.hourlyRate.amount
      ? `od ${pricing.hourlyRate.amount} zł/h`
      : null;
  const methodCopy =
    methods.length > 0
      ? methods.join(', ')
      : hasExplicit
        ? 'Brak aktywnych metod'
        : 'Nie ustawiono';
  return [methodCopy, rate].filter(Boolean).join(' · ');
};

const SectionOverviewCard: React.FC<{
  title: string;
  summary: string;
  status: 'complete' | 'missing' | 'partial';
  actionLabel: string;
  onAction: () => void;
}> = ({ title, summary, status, actionLabel, onAction }) => {
  const titleId = React.useId();
  const actionLabelId = React.useId();
  const statusLabel =
    status === 'complete' ? 'Gotowe' : status === 'partial' ? 'Do sprawdzenia' : 'Do uzupełnienia';
  const statusVariant =
    status === 'complete' ? 'success' : status === 'partial' ? 'warning' : 'neutral';
  return (
    <article className="rounded-lg border border-primary-dark-300/28 bg-primary-dark-100/14 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={titleId} className="text-sm font-semibold text-primary-light-100 sm:text-[15px]">
            {title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-primary-light-300 sm:text-sm">
            {summary}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Badge size="sm" variant={statusVariant}>
            {statusLabel}
          </Badge>
          <button
            type="button"
            onClick={onAction}
            aria-labelledby={`${actionLabelId} ${titleId}`}
            className="inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md px-2 text-xs font-medium text-primary-light-300 transition-colors hover:text-primary-light-100 focus-outline"
          >
            <span id={actionLabelId}>{actionLabel}</span>
          </button>
        </div>
      </div>
    </article>
  );
};

interface ServiceProviderProfileFormProps {
  mode?: 'default' | 'onboarding';
  forceResubmissionHint?: boolean;
  onSubmittedForVerification?: (payload?: {
    verificationStatus: ServiceVerificationStatus;
  }) => void;
}

const ServiceProviderProfileForm: React.FC<ServiceProviderProfileFormProps> = ({
  mode = 'default',
  forceResubmissionHint = false,
  onSubmittedForVerification,
}) => {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    getValues,
    control,
    watch,
    setValue,
    clearErrors,
  } = useForm<ProfileFormData>({
    defaultValues: {
      specializations: [],
      serviceName: '',
      description: '',
      businessHours: createDefaultBusinessHoursFormValue(),
      availabilityOverride: availabilityOverrideToFormValue(null),
      servicePricing: createDefaultServicePricingFormValue(),
      street: '',
      city: '',
      postalCode: '',
      phoneNumber: '',
      email: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [suggestionStatus, setSuggestionStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [locationWorkflow, setLocationWorkflow] = useState<LocationWorkflowState>({
    coordinates: null,
    status: 'missing',
  });
  const [mapCenter, setMapCenter] = useState<LatLngExpression>(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_MAP_ZOOM);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<ServiceVerificationStatus>('profile_incomplete');
  const [reviewReason, setReviewReason] = useState<string | null>(null);
  const [activeSubmitIntent, setActiveSubmitIntent] = useState<
    'save' | 'submit' | 'image-save' | 'image-remove' | 'service-capabilities' | null
  >(null);
  const [expandedBusinessHoursDay, setExpandedBusinessHoursDay] =
    useState<BusinessHoursDayKey | null>(null);
  const [persistedProfileImageUrl, setPersistedProfileImageUrl] = useState<string | null>(null);
  const [persistedProfileImage, setPersistedProfileImage] =
    useState<ServiceProviderProfileImage | null>(null);
  const [profileIsTestData, setProfileIsTestData] = useState(false);
  const [pendingProfileImage, setPendingProfileImage] = useState<PendingProfileImageState | null>(
    null
  );
  const [profileImageUploadProgress, setProfileImageUploadProgress] = useState<number>(0);
  const [isProfileImagePreviewOpen, setIsProfileImagePreviewOpen] = useState(false);
  const [isProfileImageRemoveConfirmOpen, setProfileImageRemoveConfirmOpen] = useState(false);
  const profileImagePreviewTitleId = useId();
  const profileImageRemoveTitleId = useId();
  const profileImagePreviewTriggerRef = useRef<HTMLButtonElement | null>(null);
  const profileImagePreviewCloseRef = useRef<HTMLButtonElement | null>(null);
  const profileImageRemoveTriggerRef = useRef<HTMLButtonElement | null>(null);
  const profileImageRemoveCancelRef = useRef<HTMLButtonElement | null>(null);
  const profileImageMutationInFlightRef = useRef(false);
  const [serviceFulfillmentForm, setServiceFulfillmentForm] = useState<ServiceFulfillmentFormState>(
    () => createEmptyServiceFulfillmentFormState()
  );
  const [serviceFulfillmentDirty, setServiceFulfillmentDirty] = useState(false);
  const [hasExplicitServiceFulfillment, setHasExplicitServiceFulfillment] = useState(false);
  const [serviceFulfillmentState, setServiceFulfillmentState] =
    useState<ServiceFulfillmentCapabilitiesState>('empty');
  const [serviceFulfillmentIssues, setServiceFulfillmentIssues] = useState<string[]>([]);
  const [serviceCapabilities, setServiceCapabilities] = useState<ServiceCapabilities>(
    createEmptyServiceCapabilities
  );
  const [serviceCapabilitiesDirty, setServiceCapabilitiesDirty] = useState(false);
  const [activeProfileSection, setActiveProfileSection] = useState<ProfileSectionId>('overview');
  const [businessHoursPreset, setBusinessHoursPreset] = useState<BusinessHoursPreset>('custom');
  const [expandedFulfillmentDetails, setExpandedFulfillmentDetails] = useState<FulfillmentMode[]>(
    []
  );
  const profileImageInputRef = useRef<HTMLInputElement | null>(null);

  const rawSpecializations = watch('specializations', []);
  const watchedServiceName = watch('serviceName') || '';
  const watchedDescription = watch('description') || '';
  const watchedBusinessHours = watch('businessHours');
  const watchedAvailabilityOverride = watch('availabilityOverride');
  const watchedServicePricing = watch('servicePricing');
  const streetFieldValue = watch('street') || '';
  const cityFieldValue = watch('city') || '';
  const postalCodeFieldValue = watch('postalCode') || '';
  const selectedSpecializations = useMemo(
    () => dedupeSpecializationNames(rawSpecializations),
    [rawSpecializations]
  );
  const currentUserUid = String(currentUser?.uid || '').trim();
  const userRoleIdentity = getUserRoleIdentity(userProfile);
  const isServiceAccount = userRoleIdentity === UserRole.SERVICE_ADMIN;
  const servicePricingDirty = Boolean(dirtyFields.servicePricing);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const descriptionTextareaRows = isCoarsePointer ? 3 : 4;
  const descriptionTextareaMinHeight = isCoarsePointer
    ? DESCRIPTION_TEXTAREA_MIN_HEIGHT_MOBILE
    : DESCRIPTION_TEXTAREA_MIN_HEIGHT_DESKTOP;
  const descriptionFieldRegistration = register('description', {
    validate: (value) => (value || '').trim().length > 0 || 'Opis serwisu jest wymagany',
  });

  useEffect(() => {
    if (rawSpecializations.length === selectedSpecializations.length) {
      return;
    }

    setValue('specializations', selectedSpecializations, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [rawSpecializations, selectedSpecializations, setValue]);

  useEffect(() => {
    if (watchedServicePricing?.hourlyRate?.public) {
      return;
    }
    clearErrors('servicePricing.hourlyRate.amount');
  }, [clearErrors, watchedServicePricing?.hourlyRate?.public]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const pointerQuery = window.matchMedia('(pointer: coarse)');
    const syncPointerMode = () => setIsCoarsePointer(pointerQuery.matches);
    syncPointerMode();

    if (typeof pointerQuery.addEventListener === 'function') {
      pointerQuery.addEventListener('change', syncPointerMode);
      return () => pointerQuery.removeEventListener('change', syncPointerMode);
    }

    pointerQuery.addListener(syncPointerMode);
    return () => pointerQuery.removeListener(syncPointerMode);
  }, []);

  useLayoutEffect(() => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    const shouldScroll = textarea.scrollHeight > DESCRIPTION_TEXTAREA_MAX_HEIGHT;
    const nextHeight = Math.min(textarea.scrollHeight, DESCRIPTION_TEXTAREA_MAX_HEIGHT);
    textarea.style.height = `${Math.max(descriptionTextareaMinHeight, nextHeight)}px`;
    textarea.style.overflowY = shouldScroll ? 'auto' : 'hidden';
  }, [activeProfileSection, descriptionTextareaMinHeight, isCoarsePointer, watchedDescription]);

  useEffect(() => {
    const previewUrl = pendingProfileImage?.draft.previewUrl || null;
    return () => {
      if (previewUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [pendingProfileImage?.draft.previewUrl]);

  const profileImagePreviewUrl = pendingProfileImage
    ? pendingProfileImage.draft.previewUrl
    : persistedProfileImageUrl;
  const persistedProfileImageStoragePath = resolveServiceProviderImageStoragePath(
    {
      profileImage: persistedProfileImage,
      photos: [],
      isTestData: profileIsTestData,
    },
    'full'
  );

  const suggestSpecializationFn = httpsCallable(functions, 'suggestSpecialization');

  const locationValidationMessage = useMemo(() => {
    if (locationWorkflow.status === 'confirmed' && locationWorkflow.coordinates) {
      return null;
    }
    if (locationWorkflow.status === 'stale') {
      return 'Adres został zmieniony. Kliknij ponownie "Zlokalizuj wg adresu", aby potwierdzić lokalizację.';
    }
    return 'Lokalizacja serwisu jest wymagana. Uzupełnij adres i kliknij "Zlokalizuj wg adresu".';
  }, [locationWorkflow]);

  const isLocationConfirmed =
    locationWorkflow.status === 'confirmed' && locationWorkflow.coordinates !== null;
  const canEditProfileImage = !profileIsTestData;
  const hasUnsavedFormChanges = isDirty || serviceFulfillmentDirty || serviceCapabilitiesDirty;
  const availabilityMessageLength = String(watchedAvailabilityOverride?.message || '').length;
  const profileImageSummary = useMemo(() => {
    if (profileIsTestData) {
      return {
        tone: 'warning' as const,
        label: 'Profil testowy',
        copy: 'Placeholder bez własnego zdjęcia.',
      };
    }

    if (pendingProfileImage) {
      if (profileImageUploadProgress > 0 && profileImageUploadProgress < 100) {
        return {
          tone: 'info' as const,
          label: 'Przesyłanie',
          copy: `Przesyłanie: ${profileImageUploadProgress}%`,
        };
      }

      return {
        tone: 'info' as const,
        label: 'Nowe zdjęcie',
        copy: pendingProfileImage.uploaded ? 'Plik gotowy do zapisu.' : 'Oczekuje na zapis.',
      };
    }

    if (profileImagePreviewUrl) {
      return {
        tone: 'success' as const,
        label: 'Zapisane zdjęcie',
        copy: 'Widoczne publicznie i w owner preview.',
      };
    }

    return {
      tone: 'neutral' as const,
      label: 'Brak zdjęcia',
      copy: 'Po zapisie pojawi się placeholder.',
    };
  }, [pendingProfileImage, profileImagePreviewUrl, profileImageUploadProgress, profileIsTestData]);

  useEffect(() => {
    if (!profileImagePreviewUrl) {
      setIsProfileImagePreviewOpen(false);
    }
  }, [profileImagePreviewUrl]);

  const markLocationAsStale = useCallback(() => {
    setError(null);
    setSuccess(null);
    setGeocodeError(null);
    setLocationWorkflow((prev) => ({
      ...prev,
      status: 'stale',
    }));
  }, []);

  const setBusinessHoursDayEnabled = useCallback(
    (dayKey: BusinessHoursDayKey, enabled: boolean) => {
      setValue(`businessHours.weekly.${dayKey}.enabled`, enabled, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (!enabled) {
        setValue(`businessHours.weekly.${dayKey}.open`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`businessHours.weekly.${dayKey}.close`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setExpandedBusinessHoursDay((current) => (current === dayKey ? null : current));
        return;
      }

      setExpandedBusinessHoursDay(dayKey);
    },
    [setValue]
  );

  const toggleBusinessHoursDay = useCallback((dayKey: BusinessHoursDayKey) => {
    setExpandedBusinessHoursDay((current) => (current === dayKey ? null : dayKey));
  }, []);

  const updateServiceFulfillmentMode = useCallback(
    (mode: FulfillmentMode, patch: Partial<FulfillmentModeFormState>) => {
      setError(null);
      setSuccess(null);
      setServiceFulfillmentDirty(true);
      setServiceFulfillmentForm((current) => ({
        ...current,
        [mode]: {
          ...current[mode],
          ...patch,
        },
      }));
    },
    []
  );

  const hydrateFormWithProfile = useCallback(
    (profile: ServiceProvider | null) => {
      if (profile) {
        const resolvedStatus = resolveServiceVerificationStatus(profile);
        setVerificationStatus(resolvedStatus);
        setProfileIsTestData(profile.isTestData === true);
        setPersistedProfileImageUrl(resolveServiceProviderImageUrl(profile, 'full'));
        setPersistedProfileImage(profile.profileImage ?? null);
        setPendingProfileImage(null);
        setProfileImageUploadProgress(0);
        setReviewReason(
          typeof profile.verificationReviewReason === 'string' &&
            profile.verificationReviewReason.trim().length > 0
            ? profile.verificationReviewReason.trim()
            : null
        );
        const hasExplicitFulfillment = hasExplicitServiceFulfillmentConfig(profile);
        const fulfillmentCapabilities: ServiceFulfillmentCapabilities =
          resolveServiceFulfillmentCapabilities(profile);
        setHasExplicitServiceFulfillment(hasExplicitFulfillment);
        setServiceFulfillmentState(
          hasExplicitFulfillment ? fulfillmentCapabilities.state : 'empty'
        );
        setServiceFulfillmentIssues(hasExplicitFulfillment ? fulfillmentCapabilities.issues : []);
        setServiceFulfillmentForm(serviceFulfillmentToFormState(profile));
        setServiceFulfillmentDirty(false);
        setServiceCapabilities(
          cloneServiceCapabilities(profile.serviceCapabilities) || createEmptyServiceCapabilities()
        );
        setServiceCapabilitiesDirty(false);
        setBusinessHoursPreset('custom');

        reset({
          serviceName: profile.serviceName || '',
          description: profile.description || '',
          specializations: dedupeSpecializationNames(profile.specializations || []),
          businessHours: businessHoursToFormValue(profile.businessHours),
          availabilityOverride: availabilityOverrideToFormValue(profile.availabilityOverride),
          servicePricing: servicePricingToFormValue(profile),
          street: profile.address?.street || '',
          city: profile.address?.city || '',
          postalCode: profile.address?.postalCode || '',
          phoneNumber: profile.phoneNumber || '',
          email: profile.email || currentUser?.email || '',
        });

        const parsedCoordinates = toLatLngLiteral(profile.location);
        if (parsedCoordinates) {
          setLocationWorkflow({
            coordinates: parsedCoordinates,
            status: 'confirmed',
          });
          setMapCenter([parsedCoordinates.lat, parsedCoordinates.lng]);
          setMapZoom(15);
        } else {
          setLocationWorkflow({
            coordinates: null,
            status: 'missing',
          });
          setMapCenter(DEFAULT_MAP_CENTER);
          setMapZoom(DEFAULT_MAP_ZOOM);
        }
        return;
      }

      setVerificationStatus('profile_incomplete');
      setProfileIsTestData(false);
      setPersistedProfileImageUrl(null);
      setPersistedProfileImage(null);
      setPendingProfileImage(null);
      setProfileImageUploadProgress(0);
      setReviewReason(null);
      setHasExplicitServiceFulfillment(false);
      setServiceFulfillmentState('empty');
      setServiceFulfillmentIssues([]);
      setServiceFulfillmentForm(createEmptyServiceFulfillmentFormState());
      setServiceFulfillmentDirty(false);
      setServiceCapabilities(createEmptyServiceCapabilities());
      setServiceCapabilitiesDirty(false);
      setBusinessHoursPreset('custom');
      reset({
        serviceName: '',
        description: '',
        specializations: [],
        businessHours: createDefaultBusinessHoursFormValue(),
        availabilityOverride: availabilityOverrideToFormValue(null),
        servicePricing: createDefaultServicePricingFormValue(),
        street: '',
        city: '',
        postalCode: '',
        phoneNumber: '',
        email: currentUser?.email || '',
      });
      setLocationWorkflow({
        coordinates: null,
        status: 'missing',
      });
      setMapCenter(DEFAULT_MAP_CENTER);
      setMapZoom(DEFAULT_MAP_ZOOM);
    },
    [currentUser?.email, reset]
  );

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!currentUserUid) {
      setIsFetchingData(false);
      setError('Nie jesteś zalogowany.');
      return;
    }

    if (!isServiceAccount) {
      setIsFetchingData(false);
      return;
    }

    let cancelled = false;
    setIsFetchingData(true);
    setError(null);
    setSuccess(null);
    setGeocodeError(null);

    const cachedProfile = serviceProviderService.getCachedServiceProviderProfile(currentUserUid);
    if (cachedProfile !== undefined) {
      hydrateFormWithProfile(cachedProfile);
      setIsFetchingData(false);
    }

    const fetchProfile = async () => {
      try {
        const profile = await serviceProviderService.getServiceProviderProfile(currentUserUid, {
          forceRefresh: true,
        });
        if (cancelled) return;
        hydrateFormWithProfile(profile);
      } catch (fetchError) {
        if (cancelled) return;
        console.error('Błąd podczas pobierania profilu serwisu:', fetchError);
        if (cachedProfile === undefined) {
          setError('Nie udało się pobrać istniejącego profilu. Możesz utworzyć nowy.');
          hydrateFormWithProfile(null);
        }
      } finally {
        if (!cancelled) {
          setIsFetchingData(false);
        }
      }
    };

    void fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUserUid, hydrateFormWithProfile, isServiceAccount]);

  const handleGeocodeAddress = useCallback(async () => {
    const requestedAddress = normalizeAddress({
      street: getValues('street'),
      city: getValues('city'),
      postalCode: getValues('postalCode'),
    });
    const { street, city, postalCode } = requestedAddress;

    if (!street || !city || !postalCode) {
      setGeocodeError('Podaj ulicę i numer, miasto oraz kod pocztowy przed geokodowaniem.');
      return;
    }

    if (!/^\d{2}-\d{3}$/.test(postalCode)) {
      setGeocodeError('Podaj poprawny kod pocztowy w formacie XX-XXX przed geokodowaniem.');
      return;
    }

    setIsGeocoding(true);
    setGeocodeError(null);
    setError(null);
    setSuccess(null);

    try {
      const geocodedLocation = await geocodingService.geocodeAddress({
        street,
        city,
        postalCode,
      });

      if (!geocodedLocation) {
        setGeocodeError(
          'Nie znaleziono lokalizacji dla podanego adresu. Sprawdź dane i spróbuj ponownie.'
        );
        return;
      }

      const currentAddressAfterLookup = normalizeAddress({
        street: getValues('street'),
        city: getValues('city'),
        postalCode: getValues('postalCode'),
      });

      if (!areAddressesEqual(requestedAddress, currentAddressAfterLookup)) {
        setLocationWorkflow((prev) => ({
          ...prev,
          status: 'stale',
        }));
        setGeocodeError(
          'Adres zmienił się w trakcie geokodowania. Kliknij "Zlokalizuj wg adresu" ponownie.'
        );
        return;
      }

      const nextCoordinates: LatLngLiteral = {
        lat: geocodedLocation.latitude,
        lng: geocodedLocation.longitude,
      };

      setLocationWorkflow({
        coordinates: nextCoordinates,
        status: 'confirmed',
      });
      setMapCenter([nextCoordinates.lat, nextCoordinates.lng]);
      setMapZoom(CONFIRMED_MAP_ZOOM);
    } catch (geocodeErrorValue: any) {
      console.error('Błąd geokodowania adresu:', geocodeErrorValue);
      const geocodeCode = String(geocodeErrorValue?.code || '');
      if (geocodeCode.includes('invalid-argument')) {
        setGeocodeError(
          'Nie udało się znaleźć lokalizacji dla podanego adresu. Sprawdź dane i spróbuj ponownie.'
        );
      } else if (
        geocodeCode.includes('unavailable') ||
        geocodeCode.includes('deadline-exceeded') ||
        geocodeCode.includes('internal')
      ) {
        setGeocodeError(
          'Usługa geokodowania jest chwilowo niedostępna. Spróbuj ponownie za chwilę.'
        );
      } else {
        setGeocodeError(
          getFriendlyFunctionError(
            geocodeErrorValue,
            'Nie udało się geokodować adresu. Spróbuj ponownie za chwilę.'
          )
        );
      }
    } finally {
      setIsGeocoding(false);
    }
  }, [getValues]);

  const handleMapPointUpdate = useCallback(
    (latLng: LatLngLiteral) => {
      if (locationWorkflow.status !== 'confirmed') {
        setGeocodeError(
          'Najpierw potwierdź aktualny adres przyciskiem "Zlokalizuj wg adresu", a dopiero potem koryguj pinezkę na mapie.'
        );
        return;
      }
      setGeocodeError(null);
      setLocationWorkflow((prev) => ({
        coordinates: latLng,
        status: prev.status,
      }));
    },
    [locationWorkflow.status]
  );

  const getMissingSubmitReasons = (formData: ProfileFormData): string[] => {
    const reasons: string[] = [];
    if (!String(formData.serviceName || '').trim()) reasons.push('nazwa serwisu');
    if (!String(formData.description || '').trim()) reasons.push('opis serwisu');
    if (!serviceCapabilities.items.length) reasons.push('zakres usług');
    if (!String(formData.street || '').trim()) reasons.push('ulica i numer');
    if (!String(formData.city || '').trim()) reasons.push('miasto');
    if (!String(formData.postalCode || '').trim()) reasons.push('kod pocztowy');
    if (!/^\d{2}-\d{3}$/.test(String(formData.postalCode || '').trim())) {
      reasons.push('poprawny kod pocztowy (XX-XXX)');
    }
    const digitsCount = String(formData.phoneNumber || '').replace(/\D/g, '').length;
    if (digitsCount < 9) reasons.push('poprawny numer telefonu (min. 9 cyfr)');
    const businessHoursError = validateBusinessHours(
      formBusinessHoursToPayload(formData.businessHours)
    );
    if (businessHoursError) reasons.push('poprawne godziny pracy');
    if (!isLocationConfirmed || !locationWorkflow.coordinates)
      reasons.push('potwierdzona lokalizacja');
    return Array.from(new Set(reasons));
  };

  const clearPendingProfileImage = useCallback(
    (cleanupUploaded = false) => {
      const currentPending = pendingProfileImage;
      if (currentPending?.draft.previewUrl) {
        URL.revokeObjectURL(currentPending.draft.previewUrl);
      }
      if (cleanupUploaded && currentPending?.uploaded) {
        void deleteServiceProviderProfileImageUpload(currentPending.uploaded);
      }
      setPendingProfileImage(null);
      setProfileImageUploadProgress(0);
      if (profileImageInputRef.current) {
        profileImageInputRef.current.value = '';
      }
    },
    [pendingProfileImage]
  );

  const handleProfileImageFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      event.target.value = '';

      if (!file) return;
      if (!canEditProfileImage) {
        setError('Profil testowy używa placeholdera i nie obsługuje własnego zdjęcia.');
        return;
      }

      setError(null);
      setSuccess(null);

      try {
        const draft = await prepareServiceProviderProfileImageDraft(file);
        clearPendingProfileImage(Boolean(pendingProfileImage?.uploaded));
        setPendingProfileImage({ draft });
        setProfileImageUploadProgress(0);
      } catch (draftError: any) {
        console.error('Błąd przygotowania zdjęcia profilowego:', draftError);
        setError(
          getFriendlyFunctionError(
            draftError,
            'Nie udało się przygotować zdjęcia profilowego. Sprawdź format pliku i spróbuj ponownie.'
          )
        );
      }
    },
    [canEditProfileImage, clearPendingProfileImage, pendingProfileImage]
  );

  const buildProfileImageMutation = useCallback(async () => {
    if (!pendingProfileImage) return undefined;

    if (pendingProfileImage.uploaded) {
      return toServiceProviderProfileImageMutation(pendingProfileImage.uploaded);
    }

    const uploaded = await uploadServiceProviderProfileImageDraft(
      currentUserUid,
      pendingProfileImage.draft,
      setProfileImageUploadProgress
    );

    setPendingProfileImage((current) => (current ? { ...current, uploaded } : current));

    return toServiceProviderProfileImageMutation(uploaded);
  }, [currentUserUid, pendingProfileImage]);

  const handleCommitProfileImage = useCallback(
    async (mode: 'save' | 'remove') => {
      if (profileImageMutationInFlightRef.current) {
        return;
      }

      if (!currentUser) {
        setError('Musisz być zalogowany, aby zmienić zdjęcie profilowe.');
        return;
      }

      if (!isServiceAccount) {
        setError('Nie masz uprawnień do zarządzania profilem serwisu.');
        return;
      }

      if (!canEditProfileImage) {
        setError('Profil testowy używa placeholdera i nie obsługuje własnego zdjęcia.');
        return;
      }

      if (mode === 'save' && !pendingProfileImage) {
        setError('Wybierz zdjęcie, aby je zapisać.');
        return;
      }

      profileImageMutationInFlightRef.current = true;
      setLoading(true);
      setActiveSubmitIntent(mode === 'save' ? 'image-save' : 'image-remove');
      setError(null);
      setSuccess(null);
      setSuggestionStatus(null);
      setGeocodeError(null);

      try {
        const profileImage = mode === 'remove' ? null : await buildProfileImageMutation();
        if (mode === 'save' && profileImage === undefined) {
          throw new Error('Nie udało się przygotować zdjęcia profilowego do zapisu.');
        }

        const result = await serviceProviderService.saveProfile({
          profileImage,
        });

        if (result.verificationStatus) {
          setVerificationStatus(result.verificationStatus);
        }

        if (mode === 'remove' || result.profileImage === null) {
          setPersistedProfileImageUrl(null);
          setPersistedProfileImage(null);
        } else if (result.profileImage) {
          setPersistedProfileImageUrl(result.profileImage.full.url);
          setPersistedProfileImage(result.profileImage);
        } else if (mode === 'save' && pendingProfileImage?.uploaded) {
          setPersistedProfileImageUrl(pendingProfileImage.uploaded.full.url);
          setPersistedProfileImage(pendingProfileImage.uploaded);
        }

        clearPendingProfileImage(mode === 'remove');
        if (mode === 'remove') {
          setProfileImageRemoveConfirmOpen(false);
        }
        setSuccess(
          mode === 'remove'
            ? 'Zdjęcie profilowe zostało usunięte.'
            : 'Zdjęcie profilowe zostało zapisane.'
        );
      } catch (profileImageError: any) {
        console.error('Błąd zapisu zdjęcia profilowego:', profileImageError);
        setError(
          getFriendlyFunctionError(
            profileImageError,
            'Nie udało się zapisać zdjęcia profilowego. Spróbuj ponownie.'
          )
        );
      } finally {
        profileImageMutationInFlightRef.current = false;
        setLoading(false);
        setActiveSubmitIntent(null);
      }
    },
    [
      buildProfileImageMutation,
      canEditProfileImage,
      clearPendingProfileImage,
      currentUser,
      isServiceAccount,
      pendingProfileImage,
    ]
  );

  const submitProfile = async (formData: ProfileFormData, intent: 'save' | 'submit' = 'save') => {
    if (!currentUser) {
      setError('Musisz być zalogowany, aby zaktualizować profil serwisu.');
      return;
    }

    if (!isServiceAccount) {
      setError('Nie masz uprawnień do zarządzania profilem serwisu. Wymagana rola: SERVICE_ADMIN');
      return;
    }

    if (!isLocationConfirmed || !locationWorkflow.coordinates) {
      setError(locationValidationMessage || 'Lokalizacja serwisu jest wymagana.');
      return;
    }

    const shouldSubmitForVerification = mode === 'onboarding' && intent === 'submit';
    if (shouldSubmitForVerification) {
      const missingReasons = getMissingSubmitReasons(formData);
      if (missingReasons.length > 0) {
        setError(
          `Nie można jeszcze wysłać do weryfikacji. Uzupełnij: ${missingReasons.join(', ')}.`
        );
        return;
      }
    }

    setLoading(true);
    setActiveSubmitIntent(intent);
    setError(null);
    setSuccess(null);
    setSuggestionStatus(null);
    setGeocodeError(null);

    try {
      const normalizedDescription = formData.description.trim();
      const normalizedStreet = formData.street.trim();
      const normalizedCity = formData.city.trim();
      const normalizedPostalCode = formData.postalCode.trim();
      const normalizedPhoneNumber = formData.phoneNumber.trim();
      const normalizedServiceName = formData.serviceName.trim();
      const businessHours = formBusinessHoursToPayload(formData.businessHours);
      const availabilityOverride = formAvailabilityOverrideToPayload(formData.availabilityOverride);
      const businessHoursValidationError = validateBusinessHours(businessHours);
      if (businessHoursValidationError) {
        setError(businessHoursValidationError);
        return;
      }

      if (pendingProfileImage && !canEditProfileImage) {
        setError('Profil testowy używa placeholdera i nie obsługuje własnego zdjęcia.');
        return;
      }

      const shouldSendServiceFulfillment =
        serviceFulfillmentDirty ||
        (hasExplicitServiceFulfillment && serviceFulfillmentState !== 'invalid');
      const serviceFulfillmentBuild = shouldSendServiceFulfillment
        ? buildServiceFulfillmentPayload(serviceFulfillmentForm)
        : undefined;
      if (serviceFulfillmentBuild?.error) {
        setError(serviceFulfillmentBuild.error);
        return;
      }
      const servicePricingBuild = servicePricingDirty
        ? buildServicePricingPayload(formData.servicePricing)
        : undefined;
      if (servicePricingBuild?.error) {
        setError(servicePricingBuild.error);
        return;
      }
      const canonicalServiceCapabilities = toCanonicalServiceCapabilities(serviceCapabilities);
      if (canonicalServiceCapabilities.items.length === 0) {
        setError('Dodaj co najmniej jeden zakres usług.');
        setActiveProfileSection('serviceCapabilities');
        return;
      }
      const shouldSendServiceCapabilities = true;
      if (shouldSendServiceCapabilities) {
        const capabilityValidationError = validateServiceCapabilities(canonicalServiceCapabilities);
        if (capabilityValidationError) {
          setError(capabilityValidationError);
          return;
        }
      }

      const profileImageMutation = pendingProfileImage
        ? await buildProfileImageMutation()
        : undefined;
      const dataForSave = {
        serviceName: normalizedServiceName,
        description: normalizedDescription,
        specializations: selectedSpecializations,
        businessHours,
        availabilityOverride,
        address: {
          street: normalizedStreet,
          city: normalizedCity,
          postalCode: normalizedPostalCode,
        },
        phoneNumber: normalizedPhoneNumber,
        location: {
          latitude: locationWorkflow.coordinates.lat,
          longitude: locationWorkflow.coordinates.lng,
        },
        website: null,
        profileImage: profileImageMutation,
        ...(servicePricingBuild?.payload ? { servicePricing: servicePricingBuild.payload } : {}),
        ...(serviceFulfillmentBuild?.payload
          ? { serviceFulfillment: serviceFulfillmentBuild.payload }
          : {}),
        ...(shouldSendServiceCapabilities
          ? { serviceCapabilities: canonicalServiceCapabilities }
          : {}),
      };

      const result = await serviceProviderService.saveProfile(dataForSave);
      if (result.verificationStatus) {
        setVerificationStatus(result.verificationStatus);
      }
      if (result.profileImage === null) {
        setPersistedProfileImageUrl(null);
        setPersistedProfileImage(null);
      } else if (result.profileImage) {
        setPersistedProfileImageUrl(result.profileImage.full.url);
        setPersistedProfileImage(result.profileImage);
      } else if (pendingProfileImage?.uploaded) {
        setPersistedProfileImageUrl(pendingProfileImage.uploaded.full.url);
        setPersistedProfileImage(pendingProfileImage.uploaded);
      }
      reset({
        serviceName: normalizedServiceName,
        description: normalizedDescription,
        specializations: selectedSpecializations,
        street: normalizedStreet,
        city: normalizedCity,
        postalCode: normalizedPostalCode,
        phoneNumber: normalizedPhoneNumber,
        email: formData.email || currentUser.email || '',
        businessHours: formData.businessHours,
        availabilityOverride: formData.availabilityOverride,
        servicePricing: servicePricingBuild?.payload
          ? servicePricingToFormValue({ servicePricing: servicePricingBuild.payload })
          : formData.servicePricing,
      });
      if (shouldSendServiceCapabilities) {
        setServiceCapabilities(canonicalServiceCapabilities);
        setServiceCapabilitiesDirty(false);
      }

      if (shouldSubmitForVerification) {
        await serviceProviderService.submitProfileForVerification();
        setVerificationStatus('pending_approval');
        setReviewReason(null);
        setSuccess('Profil zapisany i wysłany do weryfikacji administratora.');
        onSubmittedForVerification?.({ verificationStatus: 'pending_approval' });
      } else {
        setSuccess(result.message || 'Profil serwisu został pomyślnie zaktualizowany.');
      }

      if (profileImageMutation !== undefined) {
        clearPendingProfileImage(false);
        setProfileImageUploadProgress(0);
      }
      if (serviceFulfillmentBuild?.payload) {
        setHasExplicitServiceFulfillment(true);
        setServiceFulfillmentState('available');
        setServiceFulfillmentIssues([]);
        setServiceFulfillmentDirty(false);
      }
      setLocationWorkflow((prev) =>
        prev.coordinates
          ? {
              ...prev,
              status: 'confirmed',
            }
          : prev
      );
    } catch (saveError: any) {
      console.error('Błąd zapisu profilu serwisu:', saveError);
      setError(
        getFriendlyFunctionError(
          saveError,
          'Nie udalo sie zapisac profilu serwisu. Sprobuj ponownie.'
        )
      );
    } finally {
      setLoading(false);
      setActiveSubmitIntent(null);
    }
  };

  const triggerProfileSave = () => {
    void handleSubmit((data) => submitProfile(data, 'save'))();
  };

  const triggerProfileSubmit = () => {
    void handleSubmit((data) => submitProfile(data, 'submit'))();
  };

  const handleServiceCapabilitiesSave = useCallback(
    async (nextCapabilities: ServiceCapabilities) => {
      const canonicalCapabilities = toCanonicalServiceCapabilities(nextCapabilities);
      const validationError = validateServiceCapabilities(canonicalCapabilities);
      if (validationError) {
        setError(validationError);
        return;
      }

      setLoading(true);
      setActiveSubmitIntent('service-capabilities');
      setError(null);
      setSuccess(null);
      setSuggestionStatus(null);
      setGeocodeError(null);

      try {
        const result = await serviceProviderService.saveProfile({
          serviceCapabilities: canonicalCapabilities,
        });
        setServiceCapabilities(canonicalCapabilities);
        setServiceCapabilitiesDirty(false);
        if (result.verificationStatus) {
          setVerificationStatus(result.verificationStatus);
        }
        setSuccess('Zakres usług został zapisany.');
        setActiveProfileSection('overview');
      } catch (capabilityError: any) {
        console.error('Błąd zapisu zakresu usług:', capabilityError);
        setError(
          getFriendlyFunctionError(
            capabilityError,
            'Nie udało się zapisać zakresu usług. Spróbuj ponownie.'
          )
        );
        throw capabilityError;
      } finally {
        setLoading(false);
        setActiveSubmitIntent(null);
      }
    },
    []
  );

  const applyBusinessHoursPreset = useCallback(
    (preset: BusinessHoursPreset) => {
      setBusinessHoursPreset(preset);
      if (preset === 'custom') {
        return;
      }
      setValue('businessHours', buildBusinessHoursPresetValue(preset), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setExpandedBusinessHoursDay(null);
    },
    [setValue]
  );

  const toggleFulfillmentDetails = useCallback((mode: FulfillmentMode) => {
    setExpandedFulfillmentDetails((current) => toggleInList(current, mode));
  }, []);

  const handleMissingCapabilitySuggestion = async (data: {
    type: string;
    description: string;
    wantsToOffer: boolean;
  }) => {
    setSuggestionStatus(null);
    try {
      const result: any = await suggestSpecializationFn({
        selectedCategory: `Smart Search: ${data.type}`,
        suggestedName: `${data.description}${data.wantsToOffer ? ' [provider-wants-to-offer]' : ''}`,
      });
      setSuggestionStatus({
        type: 'success',
        message: result.data.message || 'Sugestia trafiła do moderacji.',
      });
    } catch (suggestionError: any) {
      console.error('Error calling suggestSpecialization function:', suggestionError);
      const message = getFriendlyFunctionError(
        suggestionError,
        'Nie udalo sie wyslac sugestii. Sprobuj ponownie za chwile.'
      );
      setSuggestionStatus({ type: 'error', message });
      throw new Error(message);
    }
  };

  if (isFetchingData) {
    return (
      <div className="flex items-center justify-center p-10">
        <Spinner size="lg" />
        <p className="ml-4 text-primary-light-300">Ładowanie danych profilu...</p>
      </div>
    );
  }

  if (!authLoading && !isServiceAccount && !isFetchingData) {
    return (
      <Alert variant="error" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
        Nie masz uprawnień do zarządzania profilem serwisu. Wymagana rola: SERVICE_ADMIN
      </Alert>
    );
  }

  const isOnboardingMode = mode === 'onboarding';
  const showChangesRequested = verificationStatus === 'changes_requested' || forceResubmissionHint;
  const canShowProfileSaveButton = !isOnboardingMode || verificationStatus !== 'pending_approval';
  const serviceCapabilitiesComplete = serviceCapabilities.items.length > 0;
  const fulfillmentComplete =
    FULFILLMENT_MODES.some((fulfillmentMode) => serviceFulfillmentForm[fulfillmentMode]?.enabled) ||
    watchedServicePricing?.hourlyRate?.public === true;
  const availabilityComplete = hasAnyBusinessHours(watchedBusinessHours);
  const locationComplete =
    isLocationConfirmed &&
    Boolean(String(streetFieldValue).trim()) &&
    Boolean(String(cityFieldValue).trim());
  const contactComplete =
    String(watch('phoneNumber') || '').replace(/\D/g, '').length >= 9 &&
    Boolean(String(watch('email') || '').trim());
  const basicComplete = Boolean(watchedServiceName.trim()) && Boolean(watchedDescription.trim());
  const sectionCompletion = [
    serviceCapabilitiesComplete,
    fulfillmentComplete,
    availabilityComplete,
    locationComplete,
    contactComplete,
    basicComplete,
  ].filter(Boolean).length;
  const missingProfileSections = [
    !serviceCapabilitiesComplete ? 'Zakres usług' : null,
    !fulfillmentComplete ? 'Realizacja i ceny' : null,
    !availabilityComplete ? 'Dostępność' : null,
    !locationComplete ? 'Lokalizacja' : null,
    !contactComplete ? 'Kontakt' : null,
    !basicComplete ? 'Informacje podstawowe' : null,
  ].filter((section): section is string => Boolean(section));

  return (
    <>
      <form
        onSubmit={handleSubmit((data) => submitProfile(data, 'save'))}
        className="space-y-3 md:space-y-3.5"
      >
        {error && (
          <Alert variant="error" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" icon={<CheckCircleIcon className="w-5 h-5" />}>
            {success}
          </Alert>
        )}
        {suggestionStatus && (
          <Alert
            variant={suggestionStatus.type}
            icon={
              suggestionStatus.type === 'success' ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5" />
              )
            }
          >
            {suggestionStatus.message}
          </Alert>
        )}
        {isOnboardingMode && verificationStatus === 'pending_approval' && (
          <Alert variant="info">
            Profil serwisu został już wysłany do weryfikacji. Poczekaj na decyzję administratora.
          </Alert>
        )}
        {isOnboardingMode && showChangesRequested && (
          <Alert variant="warning">
            <p className="font-semibold">Profil wymaga poprawek przed zatwierdzeniem.</p>
            {reviewReason && <p className="mt-1">{reviewReason}</p>}
          </Alert>
        )}
        {isOnboardingMode && verificationStatus === 'profile_incomplete' && (
          <Alert variant="info">
            Uzupełnij dane serwisu i wyślij profil do weryfikacji, aby odblokować panel zleceń.
          </Alert>
        )}

        {activeProfileSection === 'overview' && (
          <section className="space-y-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-primary-light-100">Dane serwisu</h1>
              <p className="text-sm text-primary-light-300">
                Publiczne informacje i ustawienia, dzięki którym klienci znajdą Twój profil.
              </p>
            </div>

            {sectionCompletion < 6 && (
              <div
                className="rounded-lg border border-status-warning-500/20 bg-status-warning-500/8 px-3 py-2.5"
                data-testid="service-profile-missing-sections-alert"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-status-warning-300">
                      Do uzupełnienia
                    </p>
                    <p className="mt-1 text-sm text-primary-light-200">
                      {missingProfileSections.join(', ')}
                    </p>
                  </div>
                  <Badge variant="warning" className="shrink-0">
                    {sectionCompletion}/6 gotowe
                  </Badge>
                </div>
              </div>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              <SectionOverviewCard
                title="Zakres usług"
                summary={getServiceCapabilitiesSummary(serviceCapabilities)}
                status={serviceCapabilitiesComplete ? 'complete' : 'missing'}
                actionLabel={serviceCapabilitiesComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('serviceCapabilities')}
              />
              <SectionOverviewCard
                title="Realizacja i ceny"
                summary={getFulfillmentSummary(
                  serviceFulfillmentForm,
                  hasExplicitServiceFulfillment,
                  watchedServicePricing
                )}
                status={fulfillmentComplete ? 'complete' : 'missing'}
                actionLabel={fulfillmentComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('pricingFulfillment')}
              />
              <SectionOverviewCard
                title="Dostępność"
                summary={getAvailabilitySummary(watchedBusinessHours, watchedAvailabilityOverride)}
                status={availabilityComplete ? 'complete' : 'missing'}
                actionLabel={availabilityComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('availability')}
              />
              <SectionOverviewCard
                title="Lokalizacja"
                summary={
                  locationComplete
                    ? `${cityFieldValue || 'Miasto'} · lokalizacja potwierdzona`
                    : 'Adres i mapa wymagają potwierdzenia'
                }
                status={locationComplete ? 'complete' : 'missing'}
                actionLabel={locationComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('location')}
              />
              <SectionOverviewCard
                title="Kontakt"
                summary={contactComplete ? 'Telefon i e-mail uzupełnione' : 'Telefon i e-mail'}
                status={contactComplete ? 'complete' : 'missing'}
                actionLabel={contactComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('contact')}
              />
              <SectionOverviewCard
                title="Informacje podstawowe"
                summary="Nazwa, opis, zdjęcie"
                status={basicComplete ? 'complete' : 'missing'}
                actionLabel={basicComplete ? 'Edytuj' : 'Ustaw'}
                onAction={() => setActiveProfileSection('basic')}
              />
            </div>
          </section>
        )}

        {activeProfileSection === 'serviceCapabilities' && (
          <ServiceCapabilitiesSection
            value={serviceCapabilities}
            disabled={loading || isFetchingData}
            isSaving={loading && activeSubmitIntent === 'service-capabilities'}
            onBack={() => setActiveProfileSection('overview')}
            onSave={handleServiceCapabilitiesSave}
            onSubmitMissingSuggestion={handleMissingCapabilitySuggestion}
          />
        )}

        {activeProfileSection === 'basic' && (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setActiveProfileSection('overview')}
              >
                Dane serwisu
              </Button>
              <h2 className="text-xl font-semibold text-primary-light-100">
                Informacje podstawowe
              </h2>
            </div>

            <section className="rs-form-section space-y-3">
              <h2 className="text-base font-semibold text-primary-light-100 sm:text-lg">
                Podstawowe informacje
              </h2>
              <Input
                id="serviceName"
                label="Nazwa serwisu"
                icon={<BuildingOfficeIcon className="w-5 h-5 text-primary-light-400/80" />}
                {...register('serviceName', { required: 'Nazwa serwisu jest wymagana' })}
                error={errors.serviceName?.message}
                className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
              />
              <div>
                <FormLabel>Opis serwisu</FormLabel>
                <textarea
                  id="description"
                  rows={descriptionTextareaRows}
                  {...descriptionFieldRegistration}
                  ref={(node) => {
                    descriptionFieldRegistration.ref(node);
                    descriptionTextareaRef.current = node;
                  }}
                  className="mt-1 block w-full resize-none overflow-y-hidden rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                  placeholder="Opisz czym zajmuje się Twój serwis..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-status-error-500">{errors.description.message}</p>
                )}
              </div>
            </section>

            <section className="rs-form-section space-y-3">
              <div className="grid gap-3 max-[360px]:grid-cols-1 grid-cols-[minmax(0,1fr)_clamp(136px,39vw,166px)] items-start">
                <div className="space-y-2 min-w-0">
                  <h2 className="text-lg font-semibold text-primary-light-100">
                    Zdjęcie profilowe
                  </h2>
                  <p className="text-sm text-primary-light-300">
                    Jedno zdjęcie 1:1. JPG, PNG i WebP.
                  </p>
                  <div className="space-y-3">
                    {profileIsTestData ? (
                      <Alert variant="warning" className="!px-3 !py-2 text-xs sm:text-sm">
                        Profil testowy renderuje placeholder i nie obsługuje własnego zdjęcia.
                      </Alert>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge size="sm" variant={profileImageSummary.tone} className="shrink-0">
                          {profileImageSummary.label}
                        </Badge>
                        <span className="text-sm text-primary-light-300">
                          {profileImageSummary.copy}
                        </span>
                      </div>
                    )}

                    {pendingProfileImage && !profileIsTestData && (
                      <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/40 p-2.5 text-xs space-y-2 sm:text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge size="sm" variant="info">
                            Nowe zdjęcie oczekuje na zapis
                          </Badge>
                          <span className="text-primary-light-400">
                            {profileImageUploadProgress > 0 && profileImageUploadProgress < 100
                              ? `Przesyłanie: ${profileImageUploadProgress}%`
                              : pendingProfileImage.uploaded
                                ? 'Plik gotowy do zapisu.'
                                : 'Plik przygotowany lokalnie.'}
                          </span>
                        </div>
                        <p className="text-primary-light-300 break-words [overflow-wrap:anywhere]">
                          {pendingProfileImage.draft.sourceFileName || 'Wybrany plik'} ·{' '}
                          <span className="whitespace-nowrap">
                            {pendingProfileImage.draft.sourceWidth}×
                            {pendingProfileImage.draft.sourceHeight}
                          </span>
                        </p>
                        <p className="text-xs text-primary-light-400">
                          JPG 1:1, pełny wariant i miniatura zapisują się w{' '}
                          <code>{`serviceProviders/${currentUserUid}`}</code>
                        </p>
                        {profileImageUploadProgress > 0 && profileImageUploadProgress < 100 && (
                          <div className="h-2 overflow-hidden rounded-full bg-primary-dark-300/60">
                            <div
                              className="h-full rounded-full bg-ratuj-300 transition-all duration-200"
                              style={{ width: `${profileImageUploadProgress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="justify-self-end w-full max-w-[166px] max-[360px]:max-w-none">
                  <button
                    ref={profileImagePreviewTriggerRef}
                    type="button"
                    onClick={() => setIsProfileImagePreviewOpen(true)}
                    disabled={loading || isFetchingData}
                    aria-label="Powiększ podgląd zdjęcia profilowego"
                    className="group relative aspect-square w-full overflow-hidden rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/60 shadow-inner transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ratuj-300 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-dark-100 disabled:cursor-not-allowed disabled:opacity-90"
                  >
                    <ServiceProviderImage
                      src={profileImagePreviewUrl}
                      storagePath={pendingProfileImage ? null : persistedProfileImageStoragePath}
                      alt={watchedServiceName || 'Zdjęcie profilowe serwisu'}
                      isTestData={profileIsTestData}
                      className="h-full w-full object-cover"
                      diagnosticContext={{
                        component: 'ServiceProviderProfileForm',
                        providerId: currentUserUid,
                        variant: 'full',
                      }}
                      onRecoveredSrc={(url) => setPersistedProfileImageUrl(url)}
                    />
                    <span className="absolute inset-x-1 bottom-1 flex items-center justify-center rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium tracking-wide text-white shadow-sm transition-opacity group-hover:bg-black/65 group-focus-visible:bg-black/65 sm:text-[11px]">
                      <MagnifyingGlassPlusIcon className="mr-1 h-3 w-3" aria-hidden="true" />
                      Powiększ
                    </span>
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleProfileImageFileChange}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => profileImageInputRef.current?.click()}
                    disabled={loading || isFetchingData || profileIsTestData}
                    className="w-full sm:w-auto"
                  >
                    {pendingProfileImage
                      ? 'Zmień plik'
                      : profileImagePreviewUrl
                        ? 'Wybierz inne zdjęcie'
                        : 'Wybierz zdjęcie'}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    isLoading={loading && activeSubmitIntent === 'image-save'}
                    onClick={() => {
                      void handleCommitProfileImage('save');
                    }}
                    disabled={
                      loading || isFetchingData || profileIsTestData || !pendingProfileImage
                    }
                    className="w-full sm:w-auto"
                  >
                    Zapisz zdjęcie
                  </Button>
                  <Button
                    ref={profileImageRemoveTriggerRef}
                    type="button"
                    variant="neutral"
                    size="sm"
                    isLoading={loading && activeSubmitIntent === 'image-remove'}
                    onClick={() => {
                      setProfileImageRemoveConfirmOpen(true);
                    }}
                    disabled={
                      loading ||
                      isFetchingData ||
                      profileIsTestData ||
                      !profileImagePreviewUrl ||
                      Boolean(pendingProfileImage)
                    }
                    className="col-span-1 w-full sm:w-auto"
                  >
                    Usuń zdjęcie
                  </Button>
                  {pendingProfileImage && (
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      onClick={() => clearPendingProfileImage(true)}
                      disabled={loading || isFetchingData}
                      className="w-full sm:w-auto"
                    >
                      Anuluj
                    </Button>
                  )}
                </div>
              </div>
            </section>

            <Modal
              isOpen={isProfileImagePreviewOpen}
              onClose={() => setIsProfileImagePreviewOpen(false)}
              ariaLabelledBy={profileImagePreviewTitleId}
              initialFocusRef={profileImagePreviewCloseRef}
              returnFocusRef={profileImagePreviewTriggerRef}
              className="max-w-3xl"
            >
              <div className="flex items-start justify-between gap-3 border-b border-primary-dark-300/70 p-4 sm:p-5">
                <div className="min-w-0 space-y-1">
                  <h3
                    id={profileImagePreviewTitleId}
                    className="text-lg font-semibold text-primary-light-100"
                  >
                    Podgląd zdjęcia profilowego
                  </h3>
                  <p className="text-xs text-primary-light-400 sm:text-sm">
                    {profileImageSummary.label} · {profileImageSummary.copy}
                  </p>
                </div>
                <Button
                  ref={profileImagePreviewCloseRef}
                  type="button"
                  variant="neutral"
                  size="sm"
                  onClick={() => setIsProfileImagePreviewOpen(false)}
                  icon={<XMarkIcon className="h-4 w-4" aria-hidden="true" />}
                >
                  <span className="sr-only">Zamknij podgląd</span>
                </Button>
              </div>
              <div className="space-y-3 p-4 sm:p-5">
                <div className="overflow-hidden rounded-xl border border-primary-dark-300 bg-primary-dark-100/50">
                  <ServiceProviderImage
                    src={profileImagePreviewUrl}
                    storagePath={pendingProfileImage ? null : persistedProfileImageStoragePath}
                    alt={watchedServiceName || 'Zdjęcie profilowe serwisu'}
                    isTestData={profileIsTestData}
                    className="block max-h-[70vh] w-full object-contain"
                    loading="eager"
                    diagnosticContext={{
                      component: 'ServiceProviderProfileForm.previewModal',
                      providerId: currentUserUid,
                      variant: 'full',
                    }}
                    onRecoveredSrc={(url) => setPersistedProfileImageUrl(url)}
                  />
                </div>

                {pendingProfileImage && !profileIsTestData && (
                  <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/40 p-3 text-sm text-primary-light-300">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge size="sm" variant="info">
                        {pendingProfileImage.uploaded ? 'Plik gotowy do zapisu' : 'Plik lokalny'}
                      </Badge>
                      <span className="min-w-0 break-words text-primary-light-400">
                        {pendingProfileImage.draft.sourceFileName || 'Wybrany plik'} ·{' '}
                        {pendingProfileImage.draft.sourceWidth}×
                        {pendingProfileImage.draft.sourceHeight}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </Modal>

            <Modal
              isOpen={isProfileImageRemoveConfirmOpen}
              onClose={() => setProfileImageRemoveConfirmOpen(false)}
              ariaLabelledBy={profileImageRemoveTitleId}
              initialFocusRef={profileImageRemoveCancelRef}
              returnFocusRef={profileImageRemoveTriggerRef}
              className="max-w-md"
            >
              <div className="space-y-4 p-4 sm:p-5">
                <div className="space-y-2">
                  <h3
                    id={profileImageRemoveTitleId}
                    className="text-lg font-semibold text-primary-light-100"
                  >
                    Usunąć zdjęcie profilowe?
                  </h3>
                  <p className="text-sm leading-6 text-primary-light-300">
                    Ta akcja zapisze <code>profileImage: null</code> w profilu serwisu. Inne dane
                    profilu pozostaną bez zmian.
                  </p>
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    ref={profileImageRemoveCancelRef}
                    type="button"
                    variant="neutral"
                    onClick={() => setProfileImageRemoveConfirmOpen(false)}
                    disabled={loading}
                  >
                    Wróć
                  </Button>
                  <Button
                    type="button"
                    variant="error"
                    isLoading={loading && activeSubmitIntent === 'image-remove'}
                    onClick={() => {
                      void handleCommitProfileImage('remove');
                    }}
                    disabled={loading || isFetchingData}
                  >
                    Usuń zdjęcie
                  </Button>
                </div>
              </div>
            </Modal>
          </>
        )}

        {activeProfileSection === 'availability' && (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setActiveProfileSection('overview')}
              >
                Dane serwisu
              </Button>
              <h2 className="text-xl font-semibold text-primary-light-100">Dostępność</h2>
            </div>

            <section className="rs-form-section space-y-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
                <div className="space-y-1 min-w-0">
                  <h2 className="text-lg font-semibold text-primary-light-100">Godziny pracy</h2>
                  <p className="text-sm text-primary-light-300">
                    Sekcja jest wyłącznie informacyjna. Zgłoszenia naprawy pozostają dostępne także
                    poza godzinami pracy.
                  </p>
                  <p className="text-xs text-primary-light-400">
                    Strefa czasowa: Polska ({BUSINESS_HOURS_TIME_ZONE})
                  </p>
                </div>

                {canShowProfileSaveButton && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    isLoading={loading && activeSubmitIntent === 'save'}
                    disabled={loading || isFetchingData || isGeocoding || !hasUnsavedFormChanges}
                    className="shrink-0 self-start"
                    onClick={triggerProfileSave}
                  >
                    {loading && activeSubmitIntent === 'save'
                      ? 'Aktualizowanie...'
                      : 'Aktualizuj profil'}
                  </Button>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-4">
                {[
                  ['workdays', 'Robocze 9-17'],
                  ['everyday', 'Codziennie'],
                  ['always', '24/7'],
                  ['custom', 'Niestandardowe'],
                ].map(([preset, label]) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyBusinessHoursPreset(preset as BusinessHoursPreset)}
                    disabled={loading || isFetchingData}
                    className={`min-h-[44px] rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                      businessHoursPreset === preset
                        ? 'border-ratuj-300/75 bg-ratuj-500/15 text-primary-light-100'
                        : 'border-primary-dark-300/70 bg-primary-dark-100/45 text-primary-light-300 hover:border-primary-dark-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/20 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <h3 className="text-sm font-semibold text-primary-light-100">
                      Tymczasowa dostępność
                    </h3>
                    <p className="text-sm text-primary-light-300">
                      {watchedAvailabilityOverride?.enabled
                        ? 'Serwis pokaże klientom informację o urlopie lub przerwie.'
                        : 'Działa według godzin pracy.'}
                    </p>
                  </div>
                  <label className="flex min-h-[40px] shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 px-3 py-2 text-sm font-semibold text-primary-light-100">
                    <input
                      type="checkbox"
                      disabled={loading || isFetchingData}
                      className="h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                      {...register('availabilityOverride.enabled')}
                    />
                    Oznacz niedostępność
                  </label>
                </div>

                {watchedAvailabilityOverride?.enabled && (
                  <div className="mt-3 grid gap-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {(['vacation', 'technical_break'] as ServiceAvailabilityOverrideType[]).map(
                        (type) => (
                          <label
                            key={type}
                            className={`flex min-h-[42px] cursor-pointer items-center rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                              watchedAvailabilityOverride?.type === type
                                ? 'border-ratuj-300/70 bg-ratuj-500/15 text-primary-light-100'
                                : 'border-primary-dark-300/70 bg-primary-dark-100/45 text-primary-light-300 hover:border-primary-dark-400'
                            }`}
                          >
                            <input
                              type="radio"
                              value={type}
                              disabled={loading || isFetchingData}
                              className="sr-only"
                              {...register('availabilityOverride.type')}
                            />
                            {AVAILABILITY_OVERRIDE_TYPE_LABELS[type]}
                          </label>
                        )
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                      <div>
                        <FormLabel>Do kiedy</FormLabel>
                        <input
                          type="datetime-local"
                          disabled={loading || isFetchingData}
                          min={toDatetimeLocalValue(new Date())}
                          className="mt-1 block min-h-[40px] w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                          {...register('availabilityOverride.endsAt')}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between gap-3">
                          <FormLabel>Krótka wiadomość</FormLabel>
                          <span className="text-xs text-primary-light-400">
                            {availabilityMessageLength}/{AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH}
                          </span>
                        </div>
                        <input
                          type="text"
                          maxLength={AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH}
                          disabled={loading || isFetchingData}
                          placeholder="Opcjonalnie: wracamy po długim weekendzie"
                          className="mt-1 block min-h-[40px] w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                          {...register('availabilityOverride.message', {
                            maxLength: AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH,
                          })}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {businessHoursPreset === 'custom' && (
                <div className="overflow-hidden rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/20">
                  {BUSINESS_HOURS_DAY_ORDER.map((dayKey) => {
                    const dayField = watchedBusinessHours?.weekly?.[dayKey];
                    const dayValue = dayField || {
                      enabled: false,
                      open: '',
                      close: '',
                    };
                    const isEnabled = dayValue.enabled === true;
                    const isExpanded = expandedBusinessHoursDay === dayKey;
                    const summary = getBusinessHoursDaySummary(dayValue);
                    const summaryTone = getBusinessHoursDayTone(dayValue);
                    const openFieldError = errors.businessHours?.weekly?.[dayKey]?.open?.message;
                    const closeFieldError = errors.businessHours?.weekly?.[dayKey]?.close?.message;
                    const panelId = `business-hours-panel-${dayKey}`;

                    return (
                      <div
                        key={dayKey}
                        className={`border-b border-primary-dark-300/70 last:border-b-0 ${
                          isExpanded ? 'bg-primary-dark-500/20' : 'bg-primary-dark-200/20'
                        }`}
                      >
                        <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                          <Controller
                            name={`businessHours.weekly.${dayKey}.enabled`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                checked={field.value === true}
                                onChange={(event) => {
                                  const nextEnabled = event.target.checked;
                                  field.onChange(nextEnabled);
                                  setBusinessHoursDayEnabled(dayKey, nextEnabled);
                                }}
                                className="h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                                aria-label={`Aktywuj ${BUSINESS_HOURS_DAY_LABELS[dayKey]}`}
                              />
                            )}
                          />

                          <button
                            type="button"
                            onClick={() => toggleBusinessHoursDay(dayKey)}
                            aria-expanded={isExpanded}
                            aria-controls={panelId}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-primary-light-100">
                              {BUSINESS_HOURS_DAY_LABELS[dayKey]}
                            </span>
                            <span
                              className={`shrink-0 whitespace-nowrap text-xs font-semibold ${summaryTone}`}
                            >
                              {summary}
                            </span>
                            <ChevronDownIcon
                              className={`h-4 w-4 flex-shrink-0 text-primary-light-400 transition-transform duration-200 ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </div>

                        {isExpanded && (
                          <div
                            id={panelId}
                            className="border-t border-primary-dark-300/70 px-3 pb-3 pt-0 sm:px-4"
                          >
                            <div className="grid grid-cols-1 gap-3 pt-3 sm:grid-cols-2">
                              <Controller
                                name={`businessHours.weekly.${dayKey}.open` as const}
                                control={control}
                                rules={{
                                  validate: (value) => {
                                    const day = watchedBusinessHours?.weekly?.[dayKey];
                                    if (!day?.enabled) return true;
                                    return value.trim().length > 0 || 'Podaj godzinę otwarcia';
                                  },
                                }}
                                render={({ field }) => (
                                  <BusinessHoursTimeField
                                    id={`businessHours-${dayKey}-open`}
                                    label="Od"
                                    value={field.value || ''}
                                    disabled={!isEnabled || loading}
                                    onChange={field.onChange}
                                    error={
                                      typeof openFieldError === 'string'
                                        ? openFieldError
                                        : undefined
                                    }
                                  />
                                )}
                              />
                              <Controller
                                name={`businessHours.weekly.${dayKey}.close` as const}
                                control={control}
                                rules={{
                                  validate: (value) => {
                                    const day = watchedBusinessHours?.weekly?.[dayKey];
                                    if (!day?.enabled) return true;
                                    if (value.trim().length === 0)
                                      return 'Podaj godzinę zamknięcia';
                                    if (day.open.trim().length === 0) return true;
                                    return (
                                      day.open < value ||
                                      'Godzina zamknięcia musi być późniejsza niż otwarcia'
                                    );
                                  },
                                }}
                                render={({ field }) => (
                                  <BusinessHoursTimeField
                                    id={`businessHours-${dayKey}-close`}
                                    label="Do"
                                    value={field.value || ''}
                                    disabled={!isEnabled || loading}
                                    onChange={field.onChange}
                                    error={
                                      typeof closeFieldError === 'string'
                                        ? closeFieldError
                                        : undefined
                                    }
                                  />
                                )}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {activeProfileSection === 'pricingFulfillment' && (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setActiveProfileSection('overview')}
              >
                Dane serwisu
              </Button>
              <h2 className="text-xl font-semibold text-primary-light-100">Realizacja i ceny</h2>
            </div>
            <section className="rs-form-section space-y-3">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-primary-light-100">
                  Stawka roboczogodziny
                </h2>
                <p className="text-sm text-primary-light-300">
                  Opcjonalna informacja publiczna widoczna w profilu serwisu.
                </p>
              </div>

              <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/20 p-3 sm:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <h3 className="text-sm font-semibold text-primary-light-100">
                      Pokaż orientacyjną stawkę roboczogodziny
                    </h3>
                    <p className="text-sm text-primary-light-300">
                      To informacja orientacyjna. Ostateczna cena zależy od diagnozy i wyceny
                      zgłoszenia.
                    </p>
                  </div>
                  <label className="flex min-h-[40px] shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 px-3 py-2 text-sm font-semibold text-primary-light-100">
                    <input
                      type="checkbox"
                      disabled={loading || isFetchingData}
                      className="h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                      {...register('servicePricing.hourlyRate.public', {
                        onChange: (event) => {
                          if (event.target.checked) {
                            return;
                          }
                          setValue('servicePricing.hourlyRate.amount', '', {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        },
                      })}
                    />
                    Pokaż stawkę
                  </label>
                </div>

                {watchedServicePricing?.hourlyRate?.public && (
                  <div className="mt-4 max-w-sm">
                    <label
                      htmlFor="service-hourly-rate-amount"
                      className="block text-sm font-medium text-primary-light-200"
                    >
                      Stawka od
                    </label>
                    <div className="relative mt-1">
                      <input
                        id="service-hourly-rate-amount"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="np. 120"
                        disabled={loading || isFetchingData}
                        className={`w-full min-h-[42px] rounded-lg border bg-primary-dark-100/55 px-3 py-2 pr-16 text-sm text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70 ${
                          errors.servicePricing?.hourlyRate?.amount
                            ? 'border-status-error-500'
                            : 'border-primary-dark-300/70'
                        }`}
                        {...register('servicePricing.hourlyRate.amount', {
                          validate: (value) => {
                            if (!watchedServicePricing?.hourlyRate?.public) return true;
                            const parsedAmount = parseServiceHourlyRateAmountInput(value);
                            return parsedAmount.error || parsedAmount.amount === undefined
                              ? parsedAmount.error || 'Podaj poprawną stawkę.'
                              : true;
                          },
                        })}
                      />
                      <span className="pointer-events-none absolute inset-y-1.5 right-2 flex items-center rounded-md border border-primary-dark-300/70 bg-primary-dark-100/85 px-2 text-sm text-primary-light-100 shadow-sm">
                        zł/h
                      </span>
                    </div>
                    {errors.servicePricing?.hourlyRate?.amount?.message && (
                      <p className="mt-1 text-sm text-status-error-500">
                        {errors.servicePricing.hourlyRate.amount.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="rs-form-section space-y-3">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-primary-light-100">
                  Cennik według typu sprawy
                </h2>
                <p className="text-sm text-primary-light-300">
                  Przygotowane pod Smart Search. Pełny zapis stawek N/U/D wymaga jeszcze
                  backendowego kontraktu, więc V1 zapisuje tylko publiczną stawkę roboczogodziny.
                </p>
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                {[
                  ['Naprawa', 'Domyślna stawka albo wycena indywidualna'],
                  ['Diagnostyka', 'Płatna diagnostyka, cena od, lub bezpłatna przy naprawie'],
                  ['Usługa', 'Wycena indywidualna albo cena od'],
                ].map(([title, copy]) => (
                  <div
                    key={title}
                    aria-disabled="true"
                    className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/35 p-3 opacity-75"
                  >
                    <p className="text-sm font-semibold text-primary-light-100">{title}</p>
                    <p className="mt-1 text-sm text-primary-light-400">{copy}</p>
                    <Badge variant="neutral" size="sm" className="mt-2">
                      Do uzgodnienia w callable
                    </Badge>
                  </div>
                ))}
              </div>
            </section>

            <section className="rs-form-section space-y-3">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-primary-light-100">
                  Sposoby realizacji zleceń
                </h2>
                <p className="text-sm text-primary-light-300">
                  Wybierz metody, które serwis realnie obsługuje. Ta konfiguracja będzie źródłem
                  wyboru dla klientów w kolejnym etapie rozwoju zgłoszeń.
                </p>
              </div>

              {!hasExplicitServiceFulfillment && !serviceFulfillmentDirty && (
                <Alert variant="info" className="!px-3 !py-2 text-sm">
                  Uzupełnij sposoby realizacji, aby klienci mogli wybrać właściwą opcję przy
                  zgłoszeniu. Obecna zgodność wsteczna nie zostanie zapisana bez Twojej zmiany.
                </Alert>
              )}

              {hasExplicitServiceFulfillment && serviceFulfillmentState === 'invalid' && (
                <Alert variant="warning" className="!px-3 !py-2 text-sm">
                  Zapisana konfiguracja sposobów realizacji jest nieczytelna. Wybierz metody
                  ponownie, aby zapisać poprawną wersję.
                  {serviceFulfillmentIssues.length > 0 && (
                    <span className="mt-1 block text-xs text-primary-light-400">
                      Szczegóły: {serviceFulfillmentIssues.join(', ')}
                    </span>
                  )}
                </Alert>
              )}

              <div className="overflow-hidden rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/20">
                {FULFILLMENT_MODES.map((mode) => {
                  const modeState = serviceFulfillmentForm[mode];
                  const isEnabled = modeState.enabled;
                  const isTravelMode = requiresFulfillmentAddress(mode);
                  const instructionsId = `fulfillment-${mode}-instructions`;
                  const pricingId = `fulfillment-${mode}-pricing`;
                  const isDetailsExpanded = expandedFulfillmentDetails.includes(mode);

                  return (
                    <div
                      key={mode}
                      className={`border-b border-primary-dark-300/70 px-3 py-3 last:border-b-0 sm:px-4 ${
                        isEnabled ? 'bg-primary-dark-500/20' : 'bg-primary-dark-200/20'
                      }`}
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(event) =>
                            updateServiceFulfillmentMode(mode, { enabled: event.target.checked })
                          }
                          disabled={loading || isFetchingData}
                          className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-primary-light-100">
                            {getFulfillmentModeLabel(mode)}
                          </span>
                          <span className="mt-0.5 block text-sm text-primary-light-300">
                            {FULFILLMENT_METHOD_CONTEXT[mode]}
                          </span>
                        </span>
                      </label>

                      {isEnabled && (
                        <div className="mt-3 space-y-3 pl-7">
                          <Button
                            type="button"
                            variant="neutral"
                            size="sm"
                            onClick={() => toggleFulfillmentDetails(mode)}
                            disabled={loading || isFetchingData}
                          >
                            {isDetailsExpanded ? 'Ukryj szczegóły' : 'Edytuj szczegóły'}
                          </Button>
                          {isDetailsExpanded && (
                            <div className="space-y-3">
                              {isTravelMode && (
                                <div>
                                  <FormLabel>Koszt dojazdu</FormLabel>
                                  <select
                                    id={pricingId}
                                    aria-label={`Koszt dojazdu: ${getFulfillmentModeLabel(mode)}`}
                                    value={modeState.pricingHint}
                                    onChange={(event) =>
                                      updateServiceFulfillmentMode(mode, {
                                        pricingHint: event.target.value as FulfillmentPricingHint,
                                      })
                                    }
                                    disabled={loading || isFetchingData}
                                    className="mt-1 block w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                                  >
                                    {Object.entries(FULFILLMENT_PRICING_HINT_LABELS).map(
                                      ([value, label]) => (
                                        <option key={value} value={value}>
                                          {label}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </div>
                              )}

                              <div>
                                <div className="flex items-center justify-between gap-3">
                                  <FormLabel>Instrukcje dla klienta</FormLabel>
                                  <span className="text-xs text-primary-light-400">
                                    {modeState.instructions.length}/
                                    {SERVICE_FULFILLMENT_INSTRUCTIONS_MAX_LENGTH}
                                  </span>
                                </div>
                                <textarea
                                  id={instructionsId}
                                  aria-label={`Instrukcje: ${getFulfillmentModeLabel(mode)}`}
                                  rows={2}
                                  maxLength={SERVICE_FULFILLMENT_INSTRUCTIONS_MAX_LENGTH}
                                  value={modeState.instructions}
                                  onChange={(event) =>
                                    updateServiceFulfillmentMode(mode, {
                                      instructions: event.target.value,
                                    })
                                  }
                                  disabled={loading || isFetchingData}
                                  className="mt-1 block max-h-32 min-h-[76px] w-full resize-y rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                                  placeholder="Opcjonalnie: godziny przyjęć, zakres dojazdu, kontakt przed wizytą..."
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/25 px-3 py-2 text-sm text-primary-light-300">
                <span className="font-medium text-primary-light-100">Płatność:</span> gotówka /
                rozliczenie bezpośrednie z serwisem.
              </div>
            </section>
          </>
        )}

        {activeProfileSection === 'location' && (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setActiveProfileSection('overview')}
              >
                Dane serwisu
              </Button>
              <h2 className="text-xl font-semibold text-primary-light-100">Lokalizacja</h2>
            </div>
            <section className="rs-form-section space-y-3">
              <h2 className="text-lg font-semibold text-primary-light-100">Lokalizacja</h2>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 items-start">
                <div className="space-y-3.5 lg:col-span-7">
                  <div className="space-y-3">
                    <Input
                      id="street"
                      label="Ulica i numer"
                      {...register('street', {
                        validate: (value) =>
                          (value || '').trim().length > 0 || 'Ulica i numer są wymagane',
                        onChange: markLocationAsStale,
                      })}
                      error={errors.street?.message}
                      disabled={loading || isGeocoding}
                      title={streetFieldValue || undefined}
                      className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2.4fr)_minmax(160px,1fr)] gap-3">
                      <Input
                        id="city"
                        label="Miasto"
                        {...register('city', {
                          validate: (value) =>
                            (value || '').trim().length > 0 || 'Miasto jest wymagane',
                          onChange: markLocationAsStale,
                        })}
                        error={errors.city?.message}
                        disabled={loading || isGeocoding}
                        title={cityFieldValue || undefined}
                        className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
                      />
                      <Input
                        id="postalCode"
                        label="Kod pocztowy"
                        {...register('postalCode', {
                          required: 'Kod pocztowy jest wymagany',
                          pattern: {
                            value: /^\d{2}-\d{3}$/,
                            message: 'Nieprawidłowy format kodu pocztowego (XX-XXX)',
                          },
                          onChange: markLocationAsStale,
                        })}
                        error={errors.postalCode?.message}
                        disabled={loading || isGeocoding}
                        title={postalCodeFieldValue || undefined}
                        className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-primary-dark-300/80 bg-primary-dark-200/40 p-2.5 sm:p-3.5 space-y-3">
                    <p className="text-sm text-primary-light-300">
                      Najpierw wpisz pełny adres i kliknij przycisk geokodowania. Po potwierdzeniu
                      możesz skorygować pozycję tapnięciem w mapę.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleGeocodeAddress}
                      disabled={isGeocoding || loading}
                      icon={
                        isGeocoding ? (
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        ) : (
                          <MapPinIcon className="w-5 h-5" />
                        )
                      }
                      className="w-full sm:w-auto"
                    >
                      {isGeocoding ? 'Lokalizowanie...' : 'Zlokalizuj wg adresu'}
                    </Button>
                    {geocodeError && <Alert variant="error">{geocodeError}</Alert>}
                    {locationWorkflow.status === 'confirmed' && isLocationConfirmed && (
                      <Alert variant="success">
                        Lokalizacja jest potwierdzona i gotowa do zapisu. Kliknięcie w mapę pozwala
                        poprawić pinezkę.
                      </Alert>
                    )}
                    {locationWorkflow.status === 'stale' && (
                      <Alert variant="warning">{locationValidationMessage}</Alert>
                    )}
                    {locationWorkflow.status === 'missing' && (
                      <Alert variant="warning">{locationValidationMessage}</Alert>
                    )}
                  </div>
                </div>

                <div className="space-y-2 lg:col-span-5">
                  <div className="h-[220px] overflow-hidden rounded-lg border border-primary-dark-300/70 sm:h-[280px] md:h-[340px]">
                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      scrollWheelZoom={!isCoarsePointer && locationWorkflow.status === 'confirmed'}
                      style={{ height: '100%', width: '100%' }}
                      className="z-0"
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapViewUpdater center={mapCenter} zoom={mapZoom} />
                      <MapLocationMarker
                        coordinates={locationWorkflow.coordinates}
                        onMapClick={handleMapPointUpdate}
                        onMarkerDragEnd={handleMapPointUpdate}
                        markerDraggable={
                          !isCoarsePointer && locationWorkflow.status === 'confirmed'
                        }
                      />
                    </MapContainer>
                  </div>
                  <p className="text-xs text-primary-light-400">
                    {locationWorkflow.status === 'confirmed'
                      ? isCoarsePointer
                        ? 'Na mobile możesz skorygować lokalizację tapnięciem w mapę. Przeciąganie pinezki pozostaje wyłączone dla stabilności.'
                        : 'Na desktopie możesz kliknąć mapę albo przeciągnąć pinezkę, aby skorygować pozycję.'
                      : 'Korekta mapy odblokuje się po potwierdzeniu aktualnego adresu przyciskiem "Zlokalizuj wg adresu".'}
                  </p>
                </div>
              </div>
            </section>
          </>
        )}

        {activeProfileSection === 'contact' && (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setActiveProfileSection('overview')}
              >
                Dane serwisu
              </Button>
              <h2 className="text-xl font-semibold text-primary-light-100">Kontakt</h2>
            </div>
            <section className="rs-form-section space-y-3">
              <h2 className="text-lg font-semibold text-primary-light-100">Kontakt</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  id="phoneNumber"
                  label="Numer telefonu"
                  icon={<DevicePhoneMobileIcon className="w-5 h-5 text-primary-light-400/80" />}
                  {...register('phoneNumber', {
                    required: 'Numer telefonu jest wymagany',
                    pattern: {
                      value: /^[+]?[0-9\s\-()]*$/,
                      message: 'Nieprawidłowy format numeru telefonu',
                    },
                    validate: (value) => {
                      const digits = String(value || '').replace(/\D/g, '');
                      return digits.length >= 9 || 'Numer telefonu jest wymagany (min. 9 cyfr)';
                    },
                  })}
                  error={errors.phoneNumber?.message}
                  className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
                />
                <Input
                  id="email"
                  label="Kontaktowy adres e-mail"
                  type="email"
                  icon={<EnvelopeIcon className="w-5 h-5 text-primary-light-400/80" />}
                  {...register('email', {
                    required: 'Adres e-mail jest wymagany',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Nieprawidłowy format adresu e-mail',
                    },
                  })}
                  error={errors.email?.message}
                  className="border-primary-dark-300/70 bg-primary-dark-100/55 focus:ring-ratuj-300/70"
                  readOnly
                  disabled
                />
              </div>
            </section>
          </>
        )}

        {activeProfileSection !== 'overview' && activeProfileSection !== 'serviceCapabilities' && (
          <div
            className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 border-t border-primary-dark-300/70 bg-primary-dark-600/95 px-0 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 backdrop-blur sm:flex-row sm:justify-end sm:border-0 sm:bg-transparent sm:pb-0"
            data-product-feedback-trigger-obstruction="true"
          >
            {canShowProfileSaveButton && (
              <Button
                type="button"
                variant={isOnboardingMode ? 'neutral' : 'primary'}
                isLoading={loading && activeSubmitIntent === 'save'}
                disabled={loading || isFetchingData || isGeocoding}
                className="w-full sm:w-auto min-w-[170px]"
                onClick={triggerProfileSave}
              >
                {loading && activeSubmitIntent === 'save'
                  ? 'Zapisywanie...'
                  : isOnboardingMode
                    ? 'Zapisz szkic'
                    : 'Zapisz profil serwisu'}
              </Button>
            )}
            {isOnboardingMode && verificationStatus !== 'pending_approval' && (
              <Button
                type="button"
                variant="primary"
                isLoading={loading && activeSubmitIntent === 'submit'}
                disabled={loading || isFetchingData || isGeocoding}
                className="w-full sm:w-auto min-w-[220px]"
                onClick={triggerProfileSubmit}
              >
                {loading && activeSubmitIntent === 'submit'
                  ? 'Wysyłanie...'
                  : verificationStatus === 'changes_requested'
                    ? 'Zapisz i wyślij ponownie'
                    : 'Zapisz i wyślij do weryfikacji'}
              </Button>
            )}
          </div>
        )}
      </form>
    </>
  );
};

const MapViewUpdater: React.FC<{ center: LatLngExpression; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, map, zoom]);
  return null;
};

const MapLocationMarker: React.FC<{
  coordinates: LatLngLiteral | null;
  onMapClick: (latLng: LatLngLiteral) => void;
  onMarkerDragEnd: (latLng: LatLngLiteral) => void;
  markerDraggable: boolean;
}> = ({ coordinates, onMapClick, onMarkerDragEnd, markerDraggable }) => {
  useMapEvents({
    click(event: LeafletMouseEvent) {
      onMapClick(event.latlng);
    },
  });

  if (!coordinates) {
    return null;
  }

  return (
    <Marker
      draggable={markerDraggable}
      eventHandlers={{
        dragend: (event) => {
          const nextPosition = event.target.getLatLng();
          onMarkerDragEnd(nextPosition);
        },
      }}
      position={coordinates}
    />
  );
};

export default ServiceProviderProfileForm;
